<template>
  <div class="home">
    <!-- Page Loading State -->
    <div v-if="pageLoading" class="page-loading-overlay">
      <div class="loading-content">
        <div class="loading-spinner"></div>
        <p class="loading-text">Φόρτωση...</p>
      </div>
    </div>

    <!-- Under Construction Mode -->
    <UnderConstruction v-if="isUnderConstruction && !pageLoading" />

    <!-- Main Content (hidden when under construction or loading) -->
    <div v-show="!isUnderConstruction && !pageLoading" class="main-content fade-in">
      <!-- Hero Image Section -->
      <section
        v-if="siteContent.show_hero_image === 'true' && siteContent.hero_image"
        class="hero-image-section"
      >
        <div class="hero-image-container">
          <picture v-if="siteContent.hero_image">
            <source
              v-if="getHeroLcpWebpUrl(siteContent.hero_image)"
              type="image/webp"
              :srcset="getHeroLcpWebpUrl(siteContent.hero_image)"
            />
            <img
              :src="getHeroImageUrl(siteContent.hero_image)"
              :alt="siteContent.hero_image_title || 'Hero Image'"
              class="hero-image"
              width="1200"
              height="800"
              loading="eager"
              decoding="async"
              fetchpriority="high"
            />
          </picture>
          <div class="hero-image-overlay">
            <div class="hero-image-content">
              <h1 v-if="siteContent.hero_image_title" class="hero-image-title">
                {{ siteContent.hero_image_title }}
              </h1>
              <p v-if="siteContent.hero_image_subtitle" class="hero-image-subtitle">
                {{ siteContent.hero_image_subtitle }}
              </p>
            </div>
          </div>
        </div>
      </section>

      <!-- Extra Sections - Before Gallery -->
      <ExtraSectionsList
        :sections="activeBeforeGallery"
        :section-products="sectionProducts"
        is-first-group
        @image-click="openImageModal"
      />

      <!-- Dynamic Gallery Section (only when Hero Image is not active) -->
      <GallerySection
        v-if="siteContent.show_hero_image !== 'true' && !hasHomeBuilderGallery"
        :show-section="siteContent.show_gallery_section === 'true'"
        :gallery-images="galleryImages"
        :gallery-style="getGalleryStyle()"
        :has-more-images="hasMoreImages"
        @open-lightbox="openLightbox"
        @load-more="loadMoreImages"
      />

      <!-- Extra Sections - After Gallery -->
      <ExtraSectionsList
        :sections="activeAfterGallery"
        :section-products="sectionProducts"
        @image-click="openImageModal"
      />

      <!-- Hero Text Section -->
      <HeroTextSection
        :show-section="siteContent.show_hero_text_section === 'true'"
        :company-name="siteContent.company_name"
        :tagline="siteContent.tagline"
        :motto="siteContent.motto"
        :title-font-family="siteContent.hero_title_font_family"
        :title-font-weight="siteContent.hero_title_font_weight"
        :title-font-size="parseFloat(siteContent.hero_title_font_size) || 3.5"
        :title-letter-spacing="parseFloat(siteContent.hero_title_letter_spacing) || 0.05"
        :title-text-transform="siteContent.hero_title_text_transform"
        :title-text-shadow="siteContent.hero_title_text_shadow"
        :title-gradient-enabled="siteContent.hero_title_gradient_enabled === 'true'"
        :title-gradient-color1="siteContent.hero_title_gradient_color1"
        :title-gradient-color2="siteContent.hero_title_gradient_color2"
        :title-gradient-angle="parseInt(siteContent.hero_title_gradient_angle) || 135"
        :subtitle-font-family="siteContent.hero_subtitle_font_family"
        :subtitle-font-weight="siteContent.hero_subtitle_font_weight"
        :subtitle-font-size="parseFloat(siteContent.hero_subtitle_font_size) || 2"
        :subtitle-letter-spacing="parseFloat(siteContent.hero_subtitle_letter_spacing) || 0"
        :subtitle-text-transform="siteContent.hero_subtitle_text_transform"
        :subtitle-text-shadow="siteContent.hero_subtitle_text_shadow"
        :subtitle-gradient-enabled="siteContent.hero_subtitle_gradient_enabled === 'true'"
        :subtitle-gradient-color1="siteContent.hero_subtitle_gradient_color1"
        :subtitle-gradient-color2="siteContent.hero_subtitle_gradient_color2"
        :subtitle-gradient-angle="parseInt(siteContent.hero_subtitle_gradient_angle) || 135"
        :text-font-family="siteContent.hero_text_font_family"
        :text-font-weight="siteContent.hero_text_font_weight"
        :text-font-size="parseFloat(siteContent.hero_text_font_size) || 1.25"
        :text-letter-spacing="parseFloat(siteContent.hero_text_letter_spacing) || 0"
        :text-text-transform="siteContent.hero_text_text_transform"
        :text-text-shadow="siteContent.hero_text_text_shadow"
        :text-gradient-enabled="siteContent.hero_text_gradient_enabled === 'true'"
        :text-gradient-color1="siteContent.hero_text_gradient_color1"
        :text-gradient-color2="siteContent.hero_text_gradient_color2"
        :text-gradient-angle="parseInt(siteContent.hero_text_gradient_angle) || 135"
        :animation-effect="siteContent.hero_animation_effect"
        :animation-duration="parseInt(siteContent.hero_animation_duration) || 1000"
      />

      <!-- Extra Sections Container -->
      <section class="extra-sections">
        <div class="container-fluid">
          <!-- Extra Sections - Before Store -->
          <ExtraSectionBlock
            v-for="(section, sectionIndex) in activeBeforeStore"
            :key="section.id"
            :section="section"
            :is-first-section="sectionIndex === 0"
            :products="sectionProducts[section.id]"
            @image-click="openImageModal"
          />

          <!-- Store Information Section -->
          <StoreSection
            :show-section="siteContent.show_store_section === 'true'"
            :store-title="siteContent.store_title"
            :store-image="siteContent.store_image"
            :store-image-link="siteContent.store_image_link"
            :store-image-size="siteContent.store_image_size"
            :description1="siteContent.store_description_1"
            :description2="siteContent.store_description_2"
            :description3="siteContent.store_description_3"
            :features="siteContent.store_features"
            :conclusion="siteContent.store_conclusion"
          />

          <!-- Extra Sections - After Store -->
          <ExtraSectionBlock
            v-for="section in activeAfterStore"
            :key="section.id"
            :section="section"
            :products="sectionProducts[section.id]"
            @image-click="openImageModal"
          />
        </div>
      </section>

      <!-- Extra Sections - Before Products -->
      <ExtraSectionsList
        :sections="activeBeforeProducts"
        wrapper-class="py-2"
        :section-products="sectionProducts"
        @image-click="openImageModal"
      />

      <!-- Masonry Gallery Section -->
      <section
        v-if="siteContent.show_masonry_section === 'true' && hasMasonryContent()"
        class="optical-masonry-section"
        data-aos="fade-up"
      >
        <div class="container-fluid">
          <div class="text-center mb-5">
            <h2 class="masonry-title">
              {{ (siteContent as any).masonry_title || 'Συλλογή Εκδρομών' }}
            </h2>
            <p class="masonry-subtitle">
              {{ (siteContent as any).masonry_subtitle || 'Ανακαλύψτε τις καλύτερες εκδρομές μας' }}
            </p>
          </div>

          <div class="optical-masonry-grid">
            <!-- Masonry Item 1 -->
            <div
              v-if="(siteContent as any).masonry_item1_image"
              class="masonry-item masonry-tall masonry-card"
              @click="openImageModal(getMasonryImageUrl((siteContent as any).masonry_item1_image))"
            >
              <img
                :src="getMasonryImageUrl((siteContent as any).masonry_item1_image)"
                :alt="(siteContent as any).masonry_item1_title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <div class="masonry-content">
                  <span class="masonry-tag">{{ (siteContent as any).masonry_item1_tag }}</span>
                  <h3 class="masonry-title-card">{{ (siteContent as any).masonry_item1_title }}</h3>
                  <p class="masonry-description">
                    {{ (siteContent as any).masonry_item1_description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Masonry Item 2 -->
            <div
              v-if="(siteContent as any).masonry_item2_image"
              class="masonry-item masonry-wide masonry-card"
              @click="openImageModal(getMasonryImageUrl((siteContent as any).masonry_item2_image))"
            >
              <img
                :src="getMasonryImageUrl((siteContent as any).masonry_item2_image)"
                :alt="(siteContent as any).masonry_item2_title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <div class="masonry-content">
                  <span class="masonry-tag">{{ (siteContent as any).masonry_item2_tag }}</span>
                  <h3 class="masonry-title-card">{{ (siteContent as any).masonry_item2_title }}</h3>
                  <p class="masonry-description">
                    {{ (siteContent as any).masonry_item2_description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Masonry Item 3 -->
            <div
              v-if="(siteContent as any).masonry_item3_image"
              class="masonry-item masonry-square masonry-card"
              @click="openImageModal(getMasonryImageUrl((siteContent as any).masonry_item3_image))"
            >
              <img
                :src="getMasonryImageUrl((siteContent as any).masonry_item3_image)"
                :alt="(siteContent as any).masonry_item3_title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <div class="masonry-content">
                  <span class="masonry-tag">{{ (siteContent as any).masonry_item3_tag }}</span>
                  <h3 class="masonry-title-card">{{ (siteContent as any).masonry_item3_title }}</h3>
                  <p class="masonry-description">
                    {{ (siteContent as any).masonry_item3_description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Masonry Item 4 -->
            <div
              v-if="(siteContent as any).masonry_item4_image"
              class="masonry-item masonry-card"
              @click="openImageModal(getMasonryImageUrl((siteContent as any).masonry_item4_image))"
            >
              <img
                :src="getMasonryImageUrl((siteContent as any).masonry_item4_image)"
                :alt="(siteContent as any).masonry_item4_title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <div class="masonry-content">
                  <span class="masonry-tag">{{ (siteContent as any).masonry_item4_tag }}</span>
                  <h3 class="masonry-title-card">{{ (siteContent as any).masonry_item4_title }}</h3>
                  <p class="masonry-description">
                    {{ (siteContent as any).masonry_item4_description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Masonry Item 5 -->
            <div
              v-if="(siteContent as any).masonry_item5_image"
              class="masonry-item masonry-card"
              @click="openImageModal(getMasonryImageUrl((siteContent as any).masonry_item5_image))"
            >
              <img
                :src="getMasonryImageUrl((siteContent as any).masonry_item5_image)"
                :alt="(siteContent as any).masonry_item5_title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <div class="masonry-content">
                  <span class="masonry-tag">{{ (siteContent as any).masonry_item5_tag }}</span>
                  <h3 class="masonry-title-card">{{ (siteContent as any).masonry_item5_title }}</h3>
                  <p class="masonry-description">
                    {{ (siteContent as any).masonry_item5_description }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Masonry Item 6 -->
            <div
              v-if="(siteContent as any).masonry_item6_image"
              class="masonry-item masonry-card"
              @click="openImageModal(getMasonryImageUrl((siteContent as any).masonry_item6_image))"
            >
              <img
                :src="getMasonryImageUrl((siteContent as any).masonry_item6_image)"
                :alt="(siteContent as any).masonry_item6_title"
                class="masonry-image"
              />
              <div class="masonry-overlay">
                <div class="masonry-content">
                  <span class="masonry-tag">{{ (siteContent as any).masonry_item6_tag }}</span>
                  <h3 class="masonry-title-card">{{ (siteContent as any).masonry_item6_title }}</h3>
                  <p class="masonry-description">
                    {{ (siteContent as any).masonry_item6_description }}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <!-- Featured Posts/Blog Section -->
      <FeaturedPostsSection
        :show-section="siteContent.show_posts_section === 'true'"
        :posts="posts"
        :loading="postsLoading"
        :section-title="siteContent.posts_section_title || 'Τελευταία Άρθρα'"
        :section-subtitle="
          siteContent.posts_section_subtitle || 'Ανακαλύψτε τα πιο πρόσφατα νέα και άρθρα μας'
        "
        :wishlist-ids="postWishlistIds"
        @toggle-wishlist="togglePostWishlist"
      />

      <!-- Featured Products Section -->
      <FeaturedProductsSection
        :show-section="
          siteContent.show_products_section === 'true' && siteStyle.siteStyle.value !== 'magazine'
        "
        :products="featuredProducts"
        :loading="productsLoading"
        :wishlist-ids="productWishlistIds"
        @view-product="viewProduct"
        @toggle-wishlist="toggleWishlist"
        @add-to-cart="addProductToCart"
      />

      <!-- Extra Sections - After Products -->
      <ExtraSectionsList
        :sections="activeAfterProducts"
        :section-products="sectionProducts"
        @image-click="openImageModal"
      />

      <!-- Lightbox Modal -->
      <div v-if="lightboxOpen" class="lightbox-overlay" @click="closeLightbox">
        <div class="lightbox-container">
          <button class="lightbox-close" @click="closeLightbox">
            <i class="bi bi-x-lg"></i>
          </button>

          <button
            class="lightbox-nav lightbox-prev"
            @click.stop="prevImage"
            v-if="currentImageIndex > 0"
          >
            <i class="bi bi-chevron-left"></i>
          </button>

          <div class="lightbox-content">
            <img
              :src="`${IMAGE_BASE_URL}?type=gallery&file=${currentImage?.filename}`"
              :alt="currentImage?.alt"
              class="lightbox-image"
            />
            <div class="lightbox-info">
              <h4 class="text-white mb-2">{{ currentImage?.title }}</h4>
              <p class="text-white-50 mb-0">{{ currentImage?.description }}</p>
              <span class="badge bg-primary mt-2">{{ currentImage?.category }}</span>
            </div>
          </div>

          <button
            class="lightbox-nav lightbox-next"
            @click.stop="nextImage"
            v-if="currentImageIndex < filteredImages.length - 1"
          >
            <i class="bi bi-chevron-right"></i>
          </button>
        </div>
      </div>
    </div>

    <!-- Quick View Modal -->
    <div
      class="modal fade"
      id="quickViewModal"
      tabindex="-1"
      aria-labelledby="quickViewModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content quick-view-modal">
          <div class="modal-header border-0">
            <h5 class="modal-title d-flex align-items-center" id="quickViewModalLabel">
              <i class="bi bi-eye me-2 text-primary"></i>
              Προεπισκόπηση Εκδρομής
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>

          <div class="modal-body p-0" v-if="selectedProduct">
            <div class="row g-0">
              <!-- Product Image -->
              <div class="col-md-6">
                <div class="quick-view-image">
                  <img
                    :src="getImageUrl(selectedProduct.main_image)"
                    :alt="selectedProduct.name"
                    class="img-fluid w-100 h-100 object-cover"
                    @error="handleImageError"
                  />
                  <div class="image-overlay">
                    <span class="badge bg-primary position-absolute top-0 start-0 m-3">
                      <i class="bi bi-map me-1"></i>
                      {{ selectedProduct.category_name }}
                    </span>
                  </div>
                </div>
              </div>

              <!-- Product Details -->
              <div class="col-md-6">
                <div class="quick-view-details p-4">
                  <div class="mb-3">
                    <h4 class="product-title mb-2">{{ selectedProduct.name }}</h4>
                    <p class="text-muted mb-3">{{ selectedProduct.description }}</p>
                  </div>

                  <div class="product-info mb-4">
                    <div class="info-item d-flex align-items-center mb-2">
                      <i class="bi bi-geo-alt text-primary me-2"></i>
                      <span><strong>Προορισμός:</strong> {{ selectedProduct.category_name }}</span>
                    </div>
                    <div class="info-item d-flex align-items-center mb-2">
                      <i class="bi bi-clock text-primary me-2"></i>
                      <span
                        ><strong>Διάρκεια:</strong>
                        {{ selectedProduct.brand_name || 'Μη καθορισμένη' }}</span
                      >
                    </div>
                    <div class="info-item d-flex align-items-center mb-2">
                      <i class="bi bi-people text-primary me-2"></i>
                      <span
                        ><strong>Διαθεσιμότητα:</strong>
                        {{ (selectedProduct.stock || 0) > 0 ? 'Διαθέσιμη' : 'Μη διαθέσιμη' }}</span
                      >
                    </div>
                  </div>

                  <div class="price-section mb-4">
                    <div class="d-flex align-items-center">
                      <span class="current-price h4 text-primary mb-0 me-3">
                        €{{ selectedProduct.price?.toFixed(2) }}
                      </span>
                      <span class="text-muted">ανά άτομο</span>
                    </div>
                  </div>

                  <div class="action-buttons d-grid gap-2">
                    <button
                      class="btn btn-primary btn-lg"
                      @click="addProductToCart(selectedProduct)"
                      :disabled="(selectedProduct.stock || 0) <= 0"
                    >
                      <i class="bi bi-cart-plus me-2"></i>
                      Αγορά
                    </button>
                    <button
                      class="btn btn-outline-primary"
                      @click="goToProductPage(selectedProduct)"
                    >
                      <i class="bi bi-info-circle me-2"></i>
                      Περισσότερες Πληροφορίες
                    </button>
                  </div>

                  <div class="social-share mt-3">
                    <small class="text-muted d-block mb-2">Μοιραστείτε:</small>
                    <div class="d-flex gap-2">
                      <button class="btn btn-outline-secondary btn-sm" title="Facebook">
                        <i class="bi bi-facebook"></i>
                      </button>
                      <button class="btn btn-outline-secondary btn-sm" title="Twitter">
                        <i class="bi bi-twitter"></i>
                      </button>
                      <button class="btn btn-outline-secondary btn-sm" title="WhatsApp">
                        <i class="bi bi-whatsapp"></i>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Booking Modal -->
    <div
      class="modal fade"
      id="bookingModal"
      tabindex="-1"
      aria-labelledby="bookingModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-lg modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="bookingModalLabel">
              <i class="bi bi-calendar-check me-2 text-primary"></i>
              Κράτηση Εκδρομής
            </h5>
            <button
              type="button"
              class="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div class="modal-body">
            <div v-if="selectedBookingProduct" class="booking-form">
              <!-- Excursion Summary -->
              <div class="booking-summary mb-4">
                <div class="row align-items-center">
                  <div class="col-3">
                    <img
                      :src="getBookingImage(selectedBookingProduct)"
                      :alt="selectedBookingProduct.name"
                      class="img-fluid rounded"
                    />
                  </div>
                  <div class="col-9">
                    <h6 class="mb-1">{{ selectedBookingProduct.name }}</h6>
                    <p class="text-muted mb-1">{{ selectedBookingProduct.category_name }}</p>
                    <div class="price">
                      <span class="h5 text-primary mb-0"
                        >€{{
                          selectedBookingProduct.original_price || selectedBookingProduct.price
                        }}</span
                      >
                      <span class="text-muted"> ανά άτομο</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Booking Form -->
              <form @submit.prevent="submitBooking">
                <div class="row g-3">
                  <div class="col-12">
                    <label for="fullName" class="form-label">Ονοματεπώνυμο *</label>
                    <input
                      type="text"
                      class="form-control"
                      id="fullName"
                      v-model="bookingForm.fullName"
                      required
                    />
                  </div>
                  <div class="col-md-6">
                    <label for="email" class="form-label">Email *</label>
                    <input
                      type="email"
                      class="form-control"
                      id="email"
                      v-model="bookingForm.email"
                      required
                    />
                  </div>
                  <div class="col-md-6">
                    <label for="phone" class="form-label">Τηλέφωνο *</label>
                    <input
                      type="tel"
                      class="form-control"
                      id="phone"
                      v-model="bookingForm.phone"
                      required
                    />
                  </div>
                  <div class="col-md-6">
                    <label for="participants" class="form-label">Αριθμός ατόμων *</label>
                    <select
                      class="form-select"
                      id="participants"
                      v-model="bookingForm.participants"
                      required
                    >
                      <option value="">Επιλέξτε αριθμό</option>
                      <option v-for="i in 10" :key="i" :value="i">
                        {{ i }} {{ i === 1 ? 'άτομο' : 'άτομα' }}
                      </option>
                    </select>
                  </div>
                  <div class="col-md-6">
                    <label for="bookingDate" class="form-label">Ημερομηνία αναχώρησης *</label>
                    <input
                      type="date"
                      class="form-control"
                      id="bookingDate"
                      v-model="bookingForm.bookingDate"
                      :min="getTomorrowDate()"
                      required
                    />
                  </div>
                  <div class="col-12">
                    <label for="specialRequests" class="form-label">Ειδικές απαιτήσεις</label>
                    <textarea
                      class="form-control"
                      id="specialRequests"
                      rows="3"
                      v-model="bookingForm.specialRequests"
                      placeholder="Τυχόν ειδικές απαιτήσεις ή σχόλια..."
                    ></textarea>
                  </div>

                  <!-- Booking Total -->
                  <div
                    v-if="bookingForm.participants"
                    class="booking-total mt-4 p-3 bg-light rounded"
                  >
                    <div class="d-flex justify-content-between align-items-center">
                      <div>
                        <h6 class="mb-1">Σύνολο κόστους</h6>
                        <small class="text-muted"
                          >{{ bookingForm.participants }} άτομα × €{{
                            selectedBookingProduct.original_price || selectedBookingProduct.price
                          }}</small
                        >
                      </div>
                      <div class="h4 text-primary mb-0">
                        €{{
                          (
                            (selectedBookingProduct.original_price ||
                              selectedBookingProduct.price) * Number(bookingForm.participants)
                          ).toFixed(2)
                        }}
                      </div>
                    </div>
                  </div>

                  <!-- Submit Button -->
                  <div class="d-grid mt-4">
                    <button type="submit" class="btn btn-primary btn-lg" :disabled="bookingLoading">
                      <span
                        v-if="bookingLoading"
                        class="spinner-border spinner-border-sm me-2"
                      ></span>
                      <i v-else class="bi bi-check-circle me-2"></i>
                      {{ bookingLoading ? 'Επεξεργασία...' : 'Κράτηση Εκδρομής' }}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Extra Sections - Footer -->
    <ExtraSectionsList
      :sections="activeFooter"
      :section-products="sectionProducts"
      @image-click="openImageModal"
    />
  </div>
  <!-- /main-content -->

  <!-- Homepage Modal -->
  <HomePageModal />

  <!-- Removed invalid extra closing div -->
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount, watch, provide } from 'vue'
import { useRouter } from 'vue-router'
import { API_BASE_URL, IMAGE_BASE_URL } from '@/utils/config'
import { authFetch } from '@/utils/authFetch'
import { BASE_URL, getImageUrl } from '@/config/env'
import { metaService } from '@/services/metaService'
import { useCartStore } from '@/stores/cart'
import { useWishlistStore } from '@/stores/wishlist'
import { useUserStore } from '@/stores/user'
import { useSiteStyle } from '@/composables/useSiteStyle'
import { useExtraSections, hasGalleryWidgetAtGalleryPositions } from '@/composables/useExtraSections'
import type { ExtraSection } from '@/types/extraSection'
import { normalizeExtraSections } from '@/utils/extraSectionUtils'
import UnderConstruction from '@/components/UnderConstruction.vue'
import HomePageModal from '@/components/HomePageModal.vue'
import ExtraSectionBlock from '@/components/home/ExtraSectionBlock.vue'
import ExtraSectionsList from '@/components/home/ExtraSectionsList.vue'

// Section Components
import GallerySection from '@/components/sections/GallerySection.vue'
import HeroTextSection from '@/components/sections/HeroTextSection.vue'
import StoreSection from '@/components/sections/StoreSection.vue'
import FeaturedPostsSection from '@/components/sections/FeaturedPostsSection.vue'
import FeaturedProductsSection from '@/components/sections/FeaturedProductsSection.vue'

// Product interface
interface Product {
  id: number
  name: string
  brand_name: string
  category_name: string
  price: number
  original_price?: number
  main_image: string
  is_featured: boolean
  rating?: number
  description?: string
  stock?: number
  slug?: string
}

// Gallery Image interface
interface GalleryImage {
  id?: number
  filename: string
  title: string
  alt: string
  description: string
  category: string
  sort_order?: number
  is_active?: number
}

// Posts interface
interface Post {
  id: number
  title: string
  slug: string
  content: string
  excerpt: string
  category_id: number | null
  category_name: string
  featured_image: string
  gallery_images: string[]
  tags: string[]
  status: 'published' | 'draft' | 'archived'
  featured: boolean
  meta_title: string
  meta_description: string
  views: number
  created_at: string
  updated_at: string
}

// Gallery state
const lightboxOpen = ref(false)
const currentImageIndex = ref(0)
const visibleImagesCount = ref(12) // Show all images by default
const currentMainImageIndex = ref(0)
const currentCarouselIndex = ref(0)

// Page loading state - starts true to prevent flash of content
const pageLoading = ref(true)

// Products state
const productsLoading = ref(false)
const selectedProduct = ref<Product | null>(null)

// Booking modal state
const selectedBookingProduct = ref<Product | null>(null)
const bookingLoading = ref(false)
const bookingForm = ref({
  fullName: '',
  email: '',
  phone: '',
  participants: '',
  bookingDate: '',
  specialRequests: '',
})

// Featured products data
const featuredProducts = ref<Product[]>([])

// Posts state
const posts = ref<Post[]>([])
const postsLoading = ref(false)

// Store references
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()
const router = useRouter()
const siteStyle = useSiteStyle()

/** First ImageWidget on the page claims LCP priority (eager + fetchpriority=high) */
let lcpImagePriorityClaimed = false
provide('claimLcpImagePriority', () => {
  if (lcpImagePriorityClaimed) return false
  lcpImagePriorityClaimed = true
  return true
})

// Dynamic content data - loaded from API
const siteContent = ref({
  company_name: '',
  tagline: '',
  motto: '',
  home_meta_title: '',
  home_meta_description: '',
  home_meta_keywords: '',
  company_logo: '',
  logo_url: '',
  store_title: '',
  store_description_1: '',
  store_description_2: '',
  store_description_3: '',
  store_features: [],
  store_conclusion: '',
  store_image: '',
  store_image_link: '',
  store_image_size: 'medium' as 'tiny' | 'mini' | 'small' | 'medium' | 'large' | 'full',
  masonry_title: '',
  masonry_subtitle: '',
  extra_sections: [],
  show_store_section: 'true',
  show_products_section: 'true',
  show_hero_text_section: 'true',
  show_hero_image: 'false',
  hero_image: '',
  hero_image_title: '',
  hero_image_subtitle: '',
  show_gallery_section: 'true',
  show_posts_section: 'true',
  show_masonry_section: 'true',
  gallery_style: 'modern',
  gallery_homepage_style: 'grid',
  homepage_layout: 'default',
  posts_section_title: 'Τελευταία Άρθρα',
  posts_section_subtitle: 'Ανακαλύψτε τα πιο πρόσφατα νέα και άρθρα μας',
  posts_layout: 'grid',
  posts_limit: 6,
  under_construction_enabled: 'false',
  // Hero Text Styling Settings - Title
  hero_title_font_family: "'Inter Variable', 'Inter', sans-serif",
  hero_title_font_weight: '700',
  hero_title_font_size: '3.5',
  hero_title_letter_spacing: '0.05',
  hero_title_text_transform: 'none',
  hero_title_text_shadow: 'soft',
  hero_title_gradient_enabled: 'true',
  hero_title_gradient_color1: '#3b82f6',
  hero_title_gradient_color2: '#2563eb',
  hero_title_gradient_angle: '135',
  // Hero Text Styling Settings - Subtitle
  hero_subtitle_font_family: "'Inter Variable', 'Inter', sans-serif",
  hero_subtitle_font_weight: '600',
  hero_subtitle_font_size: '2',
  hero_subtitle_letter_spacing: '0',
  hero_subtitle_text_transform: 'none',
  hero_subtitle_text_shadow: 'none',
  hero_subtitle_gradient_enabled: 'false',
  hero_subtitle_gradient_color1: '#3b82f6',
  hero_subtitle_gradient_color2: '#2563eb',
  hero_subtitle_gradient_angle: '135',
  // Hero Text Styling Settings - Text
  hero_text_font_family: "'Inter Variable', 'Inter', sans-serif",
  hero_text_font_weight: '400',
  hero_text_font_size: '1.25',
  hero_text_letter_spacing: '0',
  hero_text_text_transform: 'none',
  hero_text_text_shadow: 'none',
  hero_text_gradient_enabled: 'false',
  hero_text_gradient_color1: '#3b82f6',
  hero_text_gradient_color2: '#2563eb',
  hero_text_gradient_angle: '135',
  // Animation (common for all)
  hero_animation_effect: 'fade-up',
  hero_animation_duration: '1000',
})

// Gallery images will be loaded from API
const galleryImages = ref<GalleryImage[]>([
  // Empty array - will be populated from API only
])

// Products and products for extra sections
const sectionProducts = ref<{ [sectionId: number]: any[] }>({})
const loadingSectionData = ref<{ [sectionId: number]: boolean }>({})

const extraSectionsRef = computed(() =>
  Array.isArray(siteContent.value.extra_sections)
    ? (siteContent.value.extra_sections as ExtraSection[])
    : [],
)

const {
  activeBeforeGallery,
  activeAfterGallery,
  activeBeforeStore,
  activeAfterStore,
  activeBeforeProducts,
  activeAfterProducts,
  activeFooter,
} = useExtraSections(extraSectionsRef)

const hasHomeBuilderGallery = computed(() =>
  hasGalleryWidgetAtGalleryPositions(extraSectionsRef.value),
)

// Computed properties
const filteredImages = computed(() => {
  // Show all images without filtering
  return galleryImages.value.slice(0, visibleImagesCount.value)
})

const hasMoreImages = computed(() => {
  return visibleImagesCount.value < galleryImages.value.length
})

const currentImage = computed(() => {
  return filteredImages.value[currentImageIndex.value]
})

const currentMainImage = computed(() => {
  return filteredImages.value[currentMainImageIndex.value]
})

// Under Construction check
const isUnderConstruction = computed(() => {
  return siteContent.value.under_construction_enabled === 'true'
})

// Post wishlist IDs
const postWishlistIds = computed(() => {
  // Force reactivity by accessing wishlistCount
  wishlistStore.wishlistCount
  return wishlistStore.wishlistItems.filter((item) => item.type === 'post').map((item) => item.id)
})

// Product wishlist IDs
const productWishlistIds = computed(() => {
  // Force reactivity by accessing wishlistCount
  wishlistStore.wishlistCount
  return wishlistStore.wishlistItems.filter((item) => item.type !== 'post').map((item) => item.id)
})

// Methods
const getMasonryImageUrl = (filename: string) => {
  if (!filename) return ''
  return `${IMAGE_BASE_URL}?type=images&file=${filename}`
}

const hasMasonryContent = () => {
  const content = siteContent.value as any
  return (
    content.masonry_item1_image ||
    content.masonry_item2_image ||
    content.masonry_item3_image ||
    content.masonry_item4_image ||
    content.masonry_item5_image ||
    content.masonry_item6_image
  )
}

const getGalleryImageUrl = (imageName: string): string => {
  return `${IMAGE_BASE_URL}?type=product&size=thumbnail&file=${imageName}`
}

const getHeroImageUrl = (imagePath: string): string => {
  if (!imagePath) return ''
  if (imagePath.startsWith('http')) return imagePath
  if (imagePath.startsWith('gallery/') || imagePath.startsWith('logos/') || imagePath.startsWith('assets/')) {
    return `${BASE_URL}/backend/uploads/${imagePath}`
  }
  if (imagePath.includes('gallery')) {
    return `${BASE_URL}/backend/uploads/gallery/${imagePath}`
  }
  return `${BASE_URL}/backend/uploads/assets/${imagePath}`
}

/** Static LCP WebP generated server-side beside the original upload */
const getHeroLcpWebpUrl = (imagePath: string): string => {
  const full = getHeroImageUrl(imagePath)
  if (!full) return ''
  try {
    const u = new URL(full, BASE_URL)
    if (!u.pathname.includes('/backend/uploads/')) return ''
    const webpPath = u.pathname.replace(/\.[a-z0-9]+$/i, '-lcp-800.webp')
    return `${u.origin}${webpPath}`
  } catch {
    return ''
  }
}

const getHeroImageSrcset = (imagePath: string): string => {
  if (!imagePath) return ''
  let fullPath = getHeroImageUrl(imagePath)
  // Convert to relative path for img proxy
  const relPath = fullPath.replace(BASE_URL, '')
  if (!relPath.startsWith('/backend/uploads/')) return ''
  const proxy = (w: number) =>
    `/backend/api/img.php?src=${encodeURIComponent(relPath)}&w=${w}&q=82`
  return `${proxy(480)} 480w, ${proxy(800)} 800w, ${proxy(1200)} 1200w`
}

const loadMoreImages = () => {
  visibleImagesCount.value += 4
}

const setMainImage = (index: number) => {
  currentMainImageIndex.value = index
}

// Image loading handlers
const handleImageError = (event: Event) => {
  const img = event.target as HTMLImageElement

  // Try fallback path if full URL failed - but only once
  if (img.src.includes('https://') && !img.dataset.fallbackTried) {
    const filename = img.src.split('/').pop()
    if (filename && filename.trim() !== '') {
      const fallbackSrc = `/backend/uploads/assets/${filename}`
      img.dataset.fallbackTried = 'true' // Mark that we tried fallback
      img.src = fallbackSrc
      return // Don't hide yet, let fallback try
    }
  }

  // If both failed or no valid filename, hide the image
  img.style.display = 'none'
}

const handleImageLoad = (event: Event) => {}

const handleProductImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  // Hide the image if it fails to load
  img.style.display = 'none'
}

// Load dynamic content from API
const loadSiteContent = async () => {
  try {
    // iOS Safari compatibility - add timeout and error handling
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    const response = await authFetch(`${API_BASE_URL}/settings.php?action=home-bootstrap`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      signal: controller.signal,
    })

    clearTimeout(timeoutId)

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()

    if (data.success) {
      // Update gallery images if available
      if (data.data.gallery && data.data.gallery.length > 0) {
        galleryImages.value = data.data.gallery
      } else {
        galleryImages.value = []
      }

      // Update site content if available
      if (data.data.content) {
        // Ensure extra_sections is an array (backend should already parse JSON)
        if (!Array.isArray(data.data.content.extra_sections)) {
          data.data.content.extra_sections = []
        } else {
          data.data.content.extra_sections = normalizeExtraSections(
            data.data.content.extra_sections,
          )
        }

        // Ensure store_features is an array (backend should already parse JSON)
        if (!Array.isArray(data.data.content.store_features)) {
          data.data.content.store_features = []
        }

        // Fix store_image path to use production URL
        if (data.data.content.store_image && data.data.content.store_image.trim() !== '') {
          const filename = data.data.content.store_image.split('/').pop()
          if (filename && filename !== '') {
            data.data.content.store_image = `${BASE_URL}/backend/uploads/assets/${filename}`
          } else {
            data.data.content.store_image = '' // Clear if empty filename
          }
        } else {
          data.data.content.store_image = '' // Clear if empty or undefined
        }

        // Ensure store_image_size has a valid value
        if (data.data.content.store_image_size) {
          const validSizes = ['tiny', 'mini', 'small', 'medium', 'large', 'full']
          if (validSizes.includes(data.data.content.store_image_size)) {
            data.data.content.store_image_size = data.data.content.store_image_size as
              | 'tiny'
              | 'mini'
              | 'small'
              | 'medium'
              | 'large'
              | 'full'
          } else {
            data.data.content.store_image_size = 'medium' as
              | 'tiny'
              | 'mini'
              | 'small'
              | 'medium'
              | 'large'
              | 'full'
          }
        } else {
          data.data.content.store_image_size = 'medium' as
            | 'tiny'
            | 'mini'
            | 'small'
            | 'medium'
            | 'large'
            | 'full'
        }

        siteContent.value = { ...siteContent.value, ...data.data.content }
        if (siteContent.value.extra_sections && Array.isArray(siteContent.value.extra_sections)) {
          siteContent.value.extra_sections.forEach((section: any) => {
            if (section.layout?.includes('products_') || section.layout?.includes('products_')) {
              loadSectionData(section)
            }
          })
        }
      } else {
      }
    } else {
    }
  } catch (error) {
    // Content will fallback to hardcoded values
  }
}

// Load featured products from API
const loadFeaturedProducts = async () => {
  try {
    productsLoading.value = true

    // iOS Safari compatibility - add timeout and proper headers
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000) // 10 second timeout

    // Get featured products only
    let response = await authFetch(
      `${API_BASE_URL}/products.php?featured=1&status=published&limit=4&sort=created_at&order=DESC`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        signal: controller.signal,
      },
    )

    clearTimeout(timeoutId)

    if (!response.ok) {
      return
    }

    let data = await response.json()

    // Check if we got published products
    // Check if we got published products
    if (data.success && Array.isArray(data.data) && data.data.length === 0) {
      const controller2 = new AbortController()
      const timeoutId2 = setTimeout(() => controller2.abort(), 10000)

      response = await authFetch(
        `${API_BASE_URL}/products.php?limit=4&sort=created_at&order=DESC`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          signal: controller2.signal,
        },
      )

      clearTimeout(timeoutId2)
      data = await response.json()
    }

    if (data.success && Array.isArray(data.data)) {
      // Map the data to match the actual database structure
      featuredProducts.value = data.data.map((product: any) => ({
        id: product.id,
        name: product.name || 'Προϊόν',
        brand_name: product.brand_name || 'Μάρκα',
        category_name: product.category_name || 'Κατηγορία',
        price: parseFloat(product.sale_price) || parseFloat(product.price) || 0,
        original_price: product.original_price ? parseFloat(product.original_price) : undefined,
        main_image: product.featured_image || 'placeholder-product.jpg',
        is_featured: product.featured == 1,
        rating: product.rating ? parseFloat(product.rating) : undefined,
        description: product.description || '',
      }))

      if (featuredProducts.value.length > 0) {
      }
    } else {
      // No featured products available
      featuredProducts.value = []
    }
  } catch (error) {
    // If error, set to empty array (no fallback products)
    featuredProducts.value = []
  } finally {
    productsLoading.value = false
  }
}

// Load featured/latest posts from API
const loadPosts = async () => {
  try {
    postsLoading.value = true

    // Use admin settings for limit
    const postsLimit = siteContent.value.posts_limit || 6

    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 10000)

    // Get featured posts first, if none then latest posts
    let response = await authFetch(
      `${API_BASE_URL}/posts.php?action=featured&limit=${postsLimit}`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        signal: controller.signal,
      },
    )

    clearTimeout(timeoutId)
    let data = await response.json()

    // If no featured posts, get latest posts
    if (!data.success || !data.data || data.data.length === 0) {
      const controller2 = new AbortController()
      const timeoutId2 = setTimeout(() => controller2.abort(), 10000)

      response = await authFetch(
        `${API_BASE_URL}/posts.php?status=published&limit=${postsLimit}&sort=created_at&order=DESC`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
          },
          signal: controller2.signal,
        },
      )

      clearTimeout(timeoutId2)
      data = await response.json()
    }

    if (data.success && Array.isArray(data.data)) {
      posts.value = data.data.map((post: any) => ({
        id: post.id,
        title: post.title || 'Άρθρο',
        slug: post.slug || '',
        content: post.content || '',
        excerpt: post.excerpt || '',
        category_id: post.category_id || null,
        category_name: post.category_name || 'Γενικά',
        featured_image: post.featured_image || 'placeholder-post.jpg',
        gallery_images: post.gallery_images || [],
        tags: post.tags || [],
        status: post.status || 'published',
        featured: post.featured || false,
        meta_title: post.meta_title || post.title,
        meta_description: post.meta_description || post.excerpt,
        views: post.views || 0,
        created_at: post.created_at || '',
        updated_at: post.updated_at || '',
      }))
    } else {
      posts.value = []
    }
  } catch (error) {
    posts.value = []
  } finally {
    postsLoading.value = false
  }
}

// Product utility methods
const formatPrice = (price: number): string => {
  return price.toFixed(2)
}

// Product action methods
const addToCart = (product: Product) => {
  cartStore.addToCart(product, 1)

  // Show success notification (you can implement toast notifications here)
}

const toggleWishlist = (product: Product) => {
  if (isInWishlist(product.id)) {
    wishlistStore.removeFromWishlist(product.id)
  } else {
    wishlistStore.addToWishlist({
      id: product.id,
      name: product.name,
      slug: product.name.toLowerCase().replace(/\s+/g, '-'),
      price: product.price,
      sale_price: product.original_price,
      featured_image: product.main_image,
      brand_name: product.brand_name,
      category_name: product.category_name,
      in_stock: true,
      description: product.description,
    })
  }
}

const isInWishlist = (εκδρομήId: number): boolean => {
  return wishlistStore.isInWishlist(εκδρομήId)
}

const viewProduct = (product: Product) => {
  // Navigate to product detail page
  try {
    router.push(`/product/${product.id}`)
  } catch (error) {}
}

const quickViewProduct = (product: Product) => {
  selectedProduct.value = product
  // Open the modal using Bootstrap
  const modalElement = document.getElementById('quickViewModal')
  if (modalElement) {
    const modal = new (window as any).bootstrap.Modal(modalElement)
    modal.show()
  }
}

const addProductToCart = (product: Product | null) => {
  if (!product) return

  cartStore.addToCart(product)

  // Show success message
  alert('Το προϊόν προστέθηκε στο καλάθι!')
}

// Get image for booking modal
const getBookingImage = (product: Product, index = 0): string => {
  if (product.main_image) {
    return `${IMAGE_BASE_URL}?type=product&size=full&file=${product.main_image}`
  }
  return `${IMAGE_BASE_URL}?type=asset&file=placeholder-product.jpg`
}

// Get tomorrow's date for booking form
const getTomorrowDate = (): string => {
  const tomorrow = new Date()
  tomorrow.setDate(tomorrow.getDate() + 1)
  return tomorrow.toISOString().split('T')[0]
}

// Post wishlist functionality
const togglePostWishlist = (post: Post) => {
  if (wishlistStore.isPostInWishlist(post.id)) {
    wishlistStore.removePostFromWishlist(post.id)
  } else {
    wishlistStore.addPostToWishlist({
      id: post.id,
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      featured_image: post.featured_image,
      category_name: post.category_name,
      created_at: post.created_at,
      views: post.views,
      type: 'post',
    })
  }
}

// Submit booking
const submitBooking = async () => {
  if (!selectedBookingProduct.value) return

  bookingLoading.value = true

  try {
    const bookingData = {
      product_id: selectedBookingProduct.value.id,
      full_name: bookingForm.value.fullName,
      email: bookingForm.value.email,
      phone: bookingForm.value.phone,
      number_of_people: Number(bookingForm.value.participants),
      special_requests: bookingForm.value.specialRequests,
      total_cost:
        (selectedBookingProduct.value.original_price || selectedBookingProduct.value.price) *
        Number(bookingForm.value.participants),
      booking_date: bookingForm.value.bookingDate,
    }

    const response = await fetch('/backend/api/settings.php?action=create_appointment', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(bookingData),
    })

    const result = await response.json()

    if (result.success) {
      // Close modal
      const modal = (window as any).bootstrap.Modal.getInstance(
        document.getElementById('bookingModal'),
      )
      modal.hide()

      // Show success message
      alert('Η κράτηση ολοκληρώθηκε επιτυχώς!')

      // Reset form
      resetBookingForm()
    } else {
      alert(result.message || 'Σφάλμα κατά την κράτηση')
    }
  } catch (error) {
    alert('Σφάλμα σύνδεσης. Παρακαλώ δοκιμάστε ξανά.')
  } finally {
    bookingLoading.value = false
  }
}

// Reset booking form
const resetBookingForm = () => {
  bookingForm.value = {
    fullName: '',
    email: '',
    phone: '',
    participants: '',
    bookingDate: '',
    specialRequests: '',
  }
}

const goToProductPage = (product: Product | null) => {
  if (!product) return
  router.push(`/product/${product.slug || product.id}`)
}

// Lightbox methods

const openLightbox = (index: number) => {
  currentImageIndex.value = index
  lightboxOpen.value = true
  document.body.style.overflow = 'hidden' // Prevent background scrolling
}

const closeLightbox = () => {
  lightboxOpen.value = false
  document.body.style.overflow = 'auto' // Restore scrolling
}

const nextImage = () => {
  if (currentImageIndex.value < filteredImages.value.length - 1) {
    currentImageIndex.value++
  }
}

const prevImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--
  }
}

// Carousel navigation methods
const nextSlide = () => {
  if (filteredImages.value.length === 0) return
  currentCarouselIndex.value = (currentCarouselIndex.value + 1) % filteredImages.value.length
}

const previousSlide = () => {
  if (filteredImages.value.length === 0) return
  currentCarouselIndex.value =
    currentCarouselIndex.value === 0
      ? filteredImages.value.length - 1
      : currentCarouselIndex.value - 1
}

const goToSlide = (index: number) => {
  currentCarouselIndex.value = index
}

// Auto-advance carousel
let carouselInterval: any = null

const startCarouselAutoplay = () => {
  if (carouselInterval) clearInterval(carouselInterval)
  carouselInterval = setInterval(() => {
    nextSlide()
  }, 5000) // Change slide every 5 seconds
}

const stopCarouselAutoplay = () => {
  if (carouselInterval) {
    clearInterval(carouselInterval)
    carouselInterval = null
  }
}

// Keyboard navigation for lightbox
const handleKeydown = (event: KeyboardEvent) => {
  if (!lightboxOpen.value) return

  switch (event.key) {
    case 'Escape':
      closeLightbox()
      break
    case 'ArrowLeft':
      prevImage()
      break
    case 'ArrowRight':
      nextImage()
      break
  }
}

// Get gallery style with fallback to homepage style
const getGalleryStyle = () => {
  const style = siteContent.value.gallery_homepage_style || 'grid'
  return style
}

// Load products for legacy extra sections with product layouts
const loadSectionProducts = async (section: any) => {
  if (!section.category_ids || section.category_ids.length === 0) return

  try {
    loadingSectionData.value[section.id] = true

    const categoryIds = section.category_ids.join(',')
    const limit = section.product_limit || 6

    const response = await authFetch(
      `${API_BASE_URL}/products.php?categories=${categoryIds}&limit=${limit}&status=published`,
      {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      },
    )

    const data = await response.json()
    if (data.success && data.data) {
      sectionProducts.value[section.id] = data.data
    }
  } catch (error) {
  } finally {
    loadingSectionData.value[section.id] = false
  }
}

// Check if section needs product/product data
const loadSectionData = (section: ExtraSection) => {
  if (section.layout?.includes('products_')) {
    loadSectionProducts(section)
  }
}

const openImageModal = (imageUrl: string) => {
  window.open(imageUrl, '_blank')
}

// Data Loading
const loadAllData = async () => {
  try {
    pageLoading.value = true

    const safetyTimeout = setTimeout(() => {
      pageLoading.value = false
    }, 15000)

    // Critical path: site content (hero) first so LCP can paint ASAP
    await loadSiteContent().catch((err) => console.error('loadSiteContent error:', err))
    pageLoading.value = false
    clearTimeout(safetyTimeout)
    updateHomeSEO()
    window.dispatchEvent(new Event('strada:home-ready'))

    // Secondary data — does not block first paint
    void Promise.all([
      loadFeaturedProducts().catch((err) => console.error('loadFeaturedProducts error:', err)),
      loadPosts().catch((err) => console.error('loadPosts error:', err)),
    ])
  } catch (error) {
    console.error('loadAllData error:', error)
    pageLoading.value = false
    window.dispatchEvent(new Event('strada:home-ready'))
  }
}

// Update homepage SEO meta tags
const updateHomeSEO = () => {
  try {
    const content = siteContent.value
    
    // Get meta title - use custom or fallback to company name
    const title = content.home_meta_title || content.company_name || 'Το Κατάστημά μου'
    
    // Get meta description - use custom or fallback to company description
    const description = content.home_meta_description || 
                       content.store_description_1 || 
                       'Η επιχείρησή μας - Αξιοπιστία και ποιότητα'
    
    // Get company logo for Open Graph image
    const logoUrl = content.logo_url ? 
                   `${BASE_URL}/backend/uploads/logos/${content.logo_url}` : 
                   `${BASE_URL}/icons/icon-512x512.png`
    
    // Get site name
    const siteName = content.company_name || 'Το Κατάστημά μου'
    
    // Update meta tags using metaService
    metaService.updatePageMeta({
      title: title,
      description: description,
      image: logoUrl,
      url: window.location.href,
      type: 'website',
      siteName: siteName,
      breadcrumbs: [
        { name: 'Αρχική', url: BASE_URL }
      ]
    })
    
    // Update meta keywords if provided
    if (content.home_meta_keywords) {
      const keywordsMeta = document.querySelector('meta[name="keywords"]')
      if (keywordsMeta) {
        keywordsMeta.setAttribute('content', content.home_meta_keywords)
      } else {
        const meta = document.createElement('meta')
        meta.name = 'keywords'
        meta.content = content.home_meta_keywords
        document.head.appendChild(meta)
      }
    }
  } catch (error) {
    console.error('Error updating home SEO:', error)
  }
}

onMounted(async () => {
  // Load all data
  await loadAllData()

  // Add keyboard event listener
  document.addEventListener('keydown', handleKeydown)

  // Start carousel autoplay if carousel style is selected
  if (siteContent.value.gallery_style === 'carousel') {
    startCarouselAutoplay()
  }

  // Force GIF animations to play on mobile devices
  const forceGifPlay = () => {
    const heroImg = document.querySelector('.hero-image') as HTMLImageElement
    if (heroImg && heroImg.src && heroImg.src.toLowerCase().endsWith('.gif')) {
      // Force browser to reload GIF
      const originalSrc = heroImg.src
      heroImg.style.display = 'none'
      heroImg.src = ''
      // Use requestAnimationFrame for better performance
      requestAnimationFrame(() => {
        heroImg.src = originalSrc
        heroImg.style.display = 'block'
      })
    }
    
    // Also check gallery images
    const galleryGifs = document.querySelectorAll('img[src*=".gif"]')
    galleryGifs.forEach((img: any) => {
      if (img.src) {
        const src = img.src
        img.src = ''
        requestAnimationFrame(() => {
          img.src = src
        })
      }
    })
  }
  
  // Run after a short delay to ensure images are loaded
  setTimeout(forceGifPlay, 1000)
  // Also run on page visibility change (when user returns to tab)
  document.addEventListener('visibilitychange', () => {
    if (!document.hidden) {
      setTimeout(forceGifPlay, 100)
    }
  })

  // Initialize AOS animations if available, or create fallback
  if (typeof window !== 'undefined') {
    if ((window as any).AOS) {
      ;(window as any).AOS.init({
        duration: 800,
        easing: 'ease-out',
        once: true,
        offset: 100,
      })
    } else {
      // Fallback animation without AOS
      setTimeout(() => {
        const elements = document.querySelectorAll('[data-aos]')
        elements.forEach((el, index) => {
          const element = el as HTMLElement
          element.style.opacity = '1'
          element.style.transform = 'translateY(0)'
          element.style.transition = 'all 0.8s ease-out'
          element.style.transitionDelay = `${index * 0.2}s`
        })
      }, 100)
    }
  }
})

onBeforeUnmount(() => {
  // Clean up keyboard event listener
  document.removeEventListener('keydown', handleKeydown)

  // Stop carousel autoplay
  stopCarouselAutoplay()

  // Reset page loading state in case component is destroyed while loading
  pageLoading.value = false
})

const animateCounter = (element: HTMLElement, target: number) => {
  let current = 0
  const increment = target / 50 // Duration control
  const timer = setInterval(() => {
    current += increment
    if (current >= target) {
      current = target
      clearInterval(timer)
    }
    element.textContent = Math.floor(current).toString()
  }, 30) // Speed control
}

// Category Icon Mapping
const getCategoryIcon = (categoryName: string) => {
  if (!categoryName) return 'bi-geo-alt'

  const category = categoryName.toLowerCase()

  // Map Greek categories to Bootstrap Icons
  if (category.includes('θάλασσα') || category.includes('παραλία') || category.includes('νησί')) {
    return 'bi-water'
  }
  if (
    category.includes('βουνό') ||
    category.includes('ορειβασία') ||
    category.includes('πεζοπορία')
  ) {
    return 'bi-mountain'
  }
  if (category.includes('πόλη') || category.includes('αστικός') || category.includes('μουσείο')) {
    return 'bi-building'
  }
  if (
    category.includes('περιπέτεια') ||
    category.includes('αδρεναλίνη') ||
    category.includes('extreme')
  ) {
    return 'bi-lightning'
  }
  if (
    category.includes('πολιτισμός') ||
    category.includes('ιστορία') ||
    category.includes('μνημείο')
  ) {
    return 'bi-book'
  }
  if (
    category.includes('γαστρονομία') ||
    category.includes('φαγητό') ||
    category.includes('κρασί')
  ) {
    return 'bi-cup-hot'
  }
  if (category.includes('φύση') || category.includes('πάρκο') || category.includes('δάσος')) {
    return 'bi-tree'
  }
  if (
    category.includes('κρουαζιέρα') ||
    category.includes('πλοίο') ||
    category.includes('ναυτιλία')
  ) {
    return 'bi-ship'
  }
  if (
    category.includes('οικογένεια') ||
    category.includes('παιδιά') ||
    category.includes('family')
  ) {
    return 'bi-people'
  }
  if (
    category.includes('φωτογραφία') ||
    category.includes('καμερα') ||
    category.includes('photo')
  ) {
    return 'bi-camera'
  }

  // Default icon for general travel/products
  return 'bi-geo-alt'
}

// Group products for carousel display
const getExcursionGroups = (products: any[], groupSize: number) => {
  if (!products || products.length === 0) return []

  const groups = []
  for (let i = 0; i < products.length; i += groupSize) {
    groups.push(products.slice(i, i + groupSize))
  }
  return groups
}

// Navigation function for services
const goToService = (serviceSlug: string) => {
  router.push(`/services/${serviceSlug}`)
}

// Posts utility functions
const getPostImageUrl = (filename: string) => {
  if (!filename) return `${IMAGE_BASE_URL}?type=post&file=uploads/imgs/images/placeholder.jpg`
  return `${IMAGE_BASE_URL}?type=post&file=${filename}`
}

const handlePostImageError = (event: Event) => {
  const img = event.target as HTMLImageElement
  img.src = `${IMAGE_BASE_URL}?type=post&file=uploads/imgs/images/placeholder.jpg`
}

const formatPostDate = (dateString: string) => {
  if (!dateString) return ''

  try {
    const date = new Date(dateString)
    return date.toLocaleDateString('el-GR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  } catch {
    return ''
  }
}

const viewPost = (post: Post) => {
  // Open post in same tab (you could create a blog detail page later)
  router.push(`/blog/${post.slug}`)
}

// Posts carousel groups - computed property for grouping posts for carousel
const postsGroups = computed(() => {
  const groupSize = 3 // 3 posts per carousel slide
  const groups = []
  for (let i = 0; i < posts.value.length; i += groupSize) {
    groups.push(posts.value.slice(i, i + groupSize))
  }
  return groups
})

// Cleanup
import { onUnmounted } from 'vue'

onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  document.body.style.overflow = 'auto' // Ensure scrolling is restored
  stopCarouselAutoplay() // Stop carousel autoplay
})
</script>

<style scoped>
/* Home Page Styles — Inter Variable (see base.css) */
@import '../assets/css/gallery-organic.css';
@import '../assets/css/gallery-3d.css';
@import '../assets/css/gallery-glitch.css';
@import '../assets/css/project-cards.css';
@import '../assets/widget-animations.css';

/* Page Loading Overlay */
.page-loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: white;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
}

.loading-content {
  text-align: center;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

.loading-text {
  color: #666;
  font-size: 1rem;
  margin: 0;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Fade-in animation for content */
.fade-in {
  animation: fadeIn 0.5s ease-in;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Hero Image Section Styles */
.hero-image-section {
  width: 100%;
  margin: 0;
  padding: 0;
  position: relative;
  overflow: hidden;
  z-index: 0;
}

.hero-image-container {
  position: relative;
  width: 100%;
  height: 500px;
  overflow: hidden;
}

.hero-image {
  width: 100%;
  height: 100%;
  aspect-ratio: 3 / 2;
  object-fit: cover;
  object-position: center;
  display: block;
}

/* GIF heroes only — avoid permanent will-change / infinite animation on JPEG LCP */
.hero-image[src$='.gif'],
.hero-image[src*='.gif?'] {
  image-rendering: auto;
  -webkit-transform: translateZ(0);
  transform: translateZ(0);
}

.hero-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to bottom,
    rgba(0, 0, 0, 0.3) 0%,
    rgba(0, 0, 0, 0.5) 100%
  );
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
}

.hero-image-content {
  max-width: 800px;
  width: 100%;
  color: white;
  animation: fadeInUp 1s ease-out;
  box-sizing: border-box;
  padding: 0 0.5rem;
}

.hero-image-title {
  font-size: clamp(1.25rem, 5.5vw, 3rem);
  font-weight: 700;
  margin-bottom: 1rem;
  text-shadow: 2px 2px 4px rgba(0, 0, 0, 0.5);
  line-height: 1.25;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

.hero-image-subtitle {
  font-size: clamp(0.95rem, 3.8vw, 1.5rem);
  font-weight: 400;
  margin: 0;
  text-shadow: 1px 1px 3px rgba(0, 0, 0, 0.5);
  line-height: 1.4;
  overflow-wrap: anywhere;
  word-break: break-word;
  max-width: 100%;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Responsive Hero Image */
@media (max-width: 1200px) {
  .hero-image-container {
    height: 450px;
  }
}

@media (max-width: 768px) {
  .hero-image-container {
    height: 350px;
  }
  
  .hero-image-overlay {
    padding: 1.25rem 1rem;
  }
}

@media (max-width: 576px) {
  .hero-image-container {
    height: 280px;
  }
  
  .hero-image-overlay {
    padding: 1rem 0.75rem;
  }
}

/* Reset any conflicting styles */
.home {
  width: 100% !important;
  margin: 0 !important;
  padding: 0 !important;
  overflow-x: hidden;
  box-sizing: border-box;
}

/* Override any global container styles */
.home .container {
  /* Removed max-width and side padding restrictions for better full-width display */
  margin: 0 auto !important;
  margin-top: 0 !important; /* body --navbar-offset clears fixed navbar */
  width: 100% !important;
  box-sizing: border-box;
}

.home .container-fluid {
  width: 100% !important;
  padding-left: 0 !important;
  padding-right: 0 !important;
  margin: 0 auto !important;
  max-width: none !important;
  box-sizing: border-box;
}

/* Ensure proper centering on all devices */
.home section {
  width: 100%;
  margin: 0 auto;
}

.home .row {
  margin-left: 0;
  margin-right: 0;
}

.home [class*='col-'] {
  padding-left: 15px;
  padding-right: 15px;
}

.gallery-section {
  margin-top: 0 !important;
  padding-top: 0;
  padding-bottom: 1rem;
}

.main-image-container {
  position: relative;
  width: 100%;
  height: 500px;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  cursor: pointer;
}

.main-gallery-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.5s ease;
}

.main-image-container:hover .main-gallery-image {
  transform: scale(1.05);
}

.main-image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    to top,
    rgba(0, 0, 0, 0.8) 0%,
    rgba(0, 0, 0, 0.4) 40%,
    transparent 70%
  );
  display: flex;
  align-items: flex-end;
  justify-content: space-between;
  padding: 0.5rem;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.main-image-container:hover .main-image-overlay {
  opacity: 1;
}

.main-overlay-content {
  flex: 1;
}

.main-image-expand {
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.main-image-expand:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

/* Thumbnail Navigation */
.thumbnail-container {
  margin-top: 0.75rem;
}

.thumbnail-scroll {
  display: flex;
  gap: 1rem;
  overflow-x: auto;
  padding: 0.25rem;
  scrollbar-width: thin;
  scrollbar-color: #3498db transparent;
}

.thumbnail-scroll::-webkit-scrollbar {
  height: 6px;
}

.thumbnail-scroll::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.1);
  border-radius: 3px;
}

.thumbnail-scroll::-webkit-scrollbar-thumb {
  background: #3498db;
  border-radius: 3px;
}

.thumbnail-item {
  position: relative;
  min-width: 120px;
  height: 120px;
  border-radius: 10px;
  overflow: hidden;
  cursor: pointer;
  transition: all 0.3s ease;
  border: 3px solid transparent;
}

.thumbnail-item.active {
  border-color: #3498db;
  transform: scale(1.05);
  box-shadow: 0 10px 25px rgba(52, 152, 219, 0.4);
}

.thumbnail-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.thumbnail-item:hover .thumbnail-image {
  transform: scale(1.1);
}

.thumbnail-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.thumbnail-item:hover .thumbnail-overlay {
  opacity: 1;
}

/* Lightbox */
.lightbox-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
  animation: fadeIn 0.3s ease;
}

.lightbox-container {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lightbox-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  max-width: 100%;
  max-height: 100%;
}

.lightbox-image {
  max-width: 80vw;
  max-height: 70vh;
  object-fit: contain;
  border-radius: 10px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
  animation: zoomIn 0.3s ease;
}

.lightbox-info {
  text-align: center;
  margin-top: 1rem;
  max-width: 500px;
}

.lightbox-close {
  position: absolute;
  top: -50px;
  right: -50px;
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.lightbox-close:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: scale(1.1);
}

.lightbox-nav {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.2);
  border: 2px solid rgba(255, 255, 255, 0.3);
  color: white;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
}

.lightbox-nav:hover {
  background: rgba(255, 255, 255, 0.3);
  transform: translateY(-50%) scale(1.1);
}

.lightbox-prev {
  left: -80px;
}

.lightbox-next {
  right: -80px;
}

/* Animations */
@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

@keyframes zoomIn {
  from {
    opacity: 0;
    transform: scale(0.5);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

/* Load More Button */
.btn-primary {
  border-radius: 25px;
  padding: 0.375rem 1rem;
  font-weight: 600;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  border: none;
  color: white;
  transition: all 0.3s ease;
  box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
}

.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(231, 76, 60, 0.4);
  background: linear-gradient(135deg, #c0392b, #e74c3c);
  color: white;
}

/* Footer Section */
/* Hero Text Section - Amazing Effects */
.hero-text-section {
  background: linear-gradient(
    135deg,
    rgba(158, 126, 0, 0.03) 0%,
    rgba(184, 148, 10, 0.05) 25%,
    rgba(212, 175, 55, 0.03) 50%,
    rgba(184, 148, 10, 0.05) 75%,
    rgba(158, 126, 0, 0.03) 100%
  );
  position: relative;
  overflow: hidden;
  min-height: 400px;
  display: flex;
  align-items: center;
}

.hero-text-wrapper {
  position: relative;
  width: 100%;
  z-index: 2;
}

/* Floating Shapes Background */
.floating-shapes {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 1;
}

.shape {
  position: absolute;
  border-radius: 50%;
  background: linear-gradient(45deg, rgba(158, 126, 0, 0.1), rgba(184, 148, 10, 0.15));
  animation: float 8s ease-in-out infinite;
}

.shape-1 {
  width: 80px;
  height: 80px;
  top: 10%;
  left: 10%;
  animation-delay: 0s;
}

.shape-2 {
  width: 120px;
  height: 120px;
  top: 60%;
  right: 15%;
  animation-delay: 2s;
}

.shape-3 {
  width: 60px;
  height: 60px;
  bottom: 20%;
  left: 20%;
  animation-delay: 4s;
}

.shape-4 {
  width: 100px;
  height: 100px;
  top: 30%;
  right: 30%;
  animation-delay: 6s;
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0) rotate(0deg);
    opacity: 0.3;
  }
  50% {
    transform: translateY(-20px) rotate(180deg);
    opacity: 0.6;
  }
}

/* Hero Company Name - Gradient & Glow */
.hero-company-name {
  font-family: var(--font-sans);
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  margin-bottom: 1.5rem;
  position: relative;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  padding: 0 1rem;
}

.text-gradient {
  background: linear-gradient(
    45deg,
    #9e7e00 0%,
    #b8940a 25%,
    #d4af37 50%,
    #b8940a 75%,
    #9e7e00 100%
  );
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientShift 4s ease-in-out infinite;
  text-shadow: 0 0 30px rgba(212, 175, 55, 0.3);
  position: relative;
}

@keyframes gradientShift {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* Hero Tagline - Typewriter Effect */
.hero-tagline {
  font-family: var(--font-sans);
  font-size: clamp(1.5rem, 3vw, 2.2rem);
  font-weight: 300;
  margin-bottom: 1rem;
  color: #2c3e50;
  position: relative;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  padding: 0 1rem;
}

.typewriter {
  position: relative;
  overflow: hidden;
  white-space: nowrap;
  animation: typing 3s steps(40) 1s both;
}

.typewriter::after {
  content: '|';
  position: absolute;
  right: 0;
  color: #9e7e00;
  animation: blink 1s infinite;
}

@keyframes typing {
  from {
    width: 0;
  }
  to {
    width: 100%;
  }
}

@keyframes blink {
  0%,
  50% {
    opacity: 1;
  }
  51%,
  100% {
    opacity: 0;
  }
}

/* Hero Motto - Pulse Effect */
.hero-motto {
  font-family: var(--font-sans);
  font-size: clamp(1.1rem, 2vw, 1.4rem);
  font-weight: 400;
  margin-bottom: 2rem;
  color: #34495e;
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
  padding: 0 1rem;
}

.pulse-text {
  animation: pulse 3s ease-in-out infinite;
  display: inline-block;
}

@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.8;
  }
  50% {
    transform: scale(1.05);
    opacity: 1;
    text-shadow: 0 0 20px rgba(158, 126, 0, 0.3);
  }
}

/* Hero Decorations */
.hero-decorations {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 1rem;
  margin-top: 2rem;
}

.decoration-line {
  height: 2px;
  width: 80px;
  background: linear-gradient(90deg, transparent, #9e7e00, transparent);
  animation: lineGlow 2s ease-in-out infinite alternate;
}

.decoration-star {
  font-size: 1.5rem;
  animation: starRotate 4s linear infinite;
  color: #d4af37;
  text-shadow: 0 0 10px rgba(212, 175, 55, 0.6);
}

@keyframes lineGlow {
  0% {
    opacity: 0.5;
    box-shadow: 0 0 5px rgba(158, 126, 0, 0.3);
  }
  100% {
    opacity: 1;
    box-shadow: 0 0 15px rgba(158, 126, 0, 0.6);
  }
}

@keyframes starRotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Extra Sections Styling */
.extra-sections {
  background: rgba(248, 249, 250, 0.5);
}

/* Make extra-sections background transparent when section has transparent_background flag */
.extra-sections:has(.transparent-background),
.extra-section.transparent-background {
  background: transparent !important;
}

.extra-sections .container-fluid:has(.transparent-background) {
  background: transparent !important;
  padding: 0 !important;
}

/* Background Pattern Styles */
.extra-section[data-pattern]::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  pointer-events: none;
  z-index: 0;
}

.extra-section > * {
  position: relative;
  z-index: 1;
}

/* Animated Balls Pattern */
@keyframes move {
  100% {
    transform: translate3d(0, 0, 1px) rotate(360deg);
  }
}

.extra-section[data-pattern='animated-balls'] .ball {
  position: absolute;
  width: 20vmin;
  height: 20vmin;
  border-radius: 50%;
  backface-visibility: hidden;
  animation: move linear infinite;
  z-index: 0;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(odd) {
  color: var(--ball-color-1, #006d5b);
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(even) {
  color: var(--ball-color-2, #ff6f61);
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(1) {
  top: 77%;
  left: 88%;
  animation-duration: 40s;
  animation-delay: -3s;
  transform-origin: 16vw -2vh;
  box-shadow: 40vmin 0 5.703076368487546vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(2) {
  top: 42%;
  left: 2%;
  animation-duration: 53s;
  animation-delay: -29s;
  transform-origin: -19vw 21vh;
  box-shadow: -40vmin 0 5.17594621519026vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(3) {
  top: 28%;
  left: 18%;
  animation-duration: 49s;
  animation-delay: -8s;
  transform-origin: -22vw 3vh;
  box-shadow: 40vmin 0 5.248179047256236vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(4) {
  top: 50%;
  left: 79%;
  animation-duration: 26s;
  animation-delay: -21s;
  transform-origin: -17vw -6vh;
  box-shadow: 40vmin 0 5.279749632220298vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(5) {
  top: 46%;
  left: 15%;
  animation-duration: 36s;
  animation-delay: -40s;
  transform-origin: 4vw 0vh;
  box-shadow: -40vmin 0 5.964309466052033vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(6) {
  top: 77%;
  left: 16%;
  animation-duration: 31s;
  animation-delay: -10s;
  transform-origin: 18vw 4vh;
  box-shadow: 40vmin 0 5.178483653434181vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(7) {
  top: 22%;
  left: 17%;
  animation-duration: 55s;
  animation-delay: -6s;
  transform-origin: 1vw -23vh;
  box-shadow: -40vmin 0 5.703026794398318vmin currentColor;
}

.extra-section[data-pattern='animated-balls'] .ball:nth-child(8) {
  top: 41%;
  left: 47%;
  animation-duration: 43s;
  animation-delay: -28s;
  transform-origin: 25vw -3vh;
  box-shadow: 40vmin 0 5.196265905749415vmin currentColor;
}

/* Rain Lines Pattern */
.extra-section[data-pattern='rain-lines'] .lines {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 100%;
  margin: auto;
  width: 90%;
  display: flex;
  justify-content: space-between;
  z-index: 0;
}

.extra-section[data-pattern='rain-lines'] .line {
  position: relative;
  width: 1px;
  height: 100%;
  overflow: hidden;
}

.extra-section[data-pattern='rain-lines'] .line::after {
  content: '';
  display: block;
  position: absolute;
  height: 15vh;
  width: 100%;
  top: -50%;
  left: 0;
  animation: drop 7s 0s infinite;
  animation-fill-mode: forwards;
  animation-timing-function: cubic-bezier(0.4, 0.26, 0, 0.97);
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(1)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ff4500 75%, #ff4500 100%);
  animation-delay: 0.5s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(2)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #32cd32 75%, #32cd32 100%);
  animation-delay: 1s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(3)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #1e90ff 75%, #1e90ff 100%);
  animation-delay: 1.5s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(4)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ffd700 75%, #ffd700 100%);
  animation-delay: 2s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(5)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #8a2be2 75%, #8a2be2 100%);
  animation-delay: 2.5s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(6)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #20b2aa 75%, #20b2aa 100%);
  animation-delay: 3s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(7)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #dc143c 75%, #dc143c 100%);
  animation-delay: 3.5s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(8)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #00fa9a 75%, #00fa9a 100%);
  animation-delay: 4s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(9)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #ff1493 75%, #ff1493 100%);
  animation-delay: 4.5s;
}

.extra-section[data-pattern='rain-lines'] .line:nth-child(10)::after {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #00bfff 75%, #00bfff 100%);
  animation-delay: 5s;
}

@keyframes drop {
  0% {
    top: -50%;
  }
  100% {
    top: 110%;
  }
}

.tagline {
  font-family: var(--font-sans);
  font-size: 22px;
  color: #006b9e;
  font-weight: 400;
  margin-bottom: 0.75rem;
  animation: slideInUp 1s ease-out;
  text-shadow: 0 1px 2px rgba(0, 107, 158, 0.2);
  letter-spacing: 0.5px;
  line-height: 1.4;
}

.motto {
  font-family: var(--font-sans);
  font-size: 22px;
  font-weight: 600;
  font-style: italic;
  background: linear-gradient(45deg, #9e7e00, #b8940a);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: fadeInUp 1s ease-out;
  text-shadow: 0 0 10px rgba(158, 126, 0, 0.3);
  letter-spacing: 0.5px;
  line-height: 1.3;
}

@keyframes slideInUp {
  0% {
    transform: translateY(30px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

@keyframes fadeInUp {
  0% {
    transform: translateY(40px);
    opacity: 0;
  }
  100% {
    transform: translateY(0);
    opacity: 1;
  }
}

/* Gradient Waves Pattern */
.extra-section[data-pattern='gradient-waves'] {
  background: linear-gradient(
    315deg,
    rgba(101, 0, 94, 1) 3%,
    rgba(60, 132, 206, 1) 38%,
    rgba(48, 238, 226, 1) 68%,
    rgba(255, 25, 25, 1) 98%
  );
  animation: gradientWaves 15s ease infinite;
  background-size: 400% 400%;
  overflow: hidden;
}

@keyframes gradientWaves {
  0% {
    background-position: 0% 0%;
  }
  50% {
    background-position: 100% 100%;
  }
  100% {
    background-position: 0% 0%;
  }
}

.extra-section[data-pattern='gradient-waves'] .wave {
  background: rgb(255 255 255 / 25%);
  border-radius: 1000% 1000% 0 0;
  position: absolute;
  width: 200%;
  height: 12em;
  animation: waveMotion 10s -3s linear infinite;
  transform: translate3d(0, 0, 0);
  opacity: 0.8;
  bottom: 0;
  left: 0;
  z-index: 0;
}

.extra-section[data-pattern='gradient-waves'] .wave:nth-of-type(2) {
  bottom: -1.25em;
  animation: waveMotion 18s linear reverse infinite;
  opacity: 0.8;
}

.extra-section[data-pattern='gradient-waves'] .wave:nth-of-type(3) {
  bottom: -2.5em;
  animation: waveMotion 20s -1s reverse infinite;
  opacity: 0.9;
}

@keyframes waveMotion {
  2% {
    transform: translateX(1);
  }
  25% {
    transform: translateX(-25%);
  }
  50% {
    transform: translateX(-50%);
  }
  75% {
    transform: translateX(-25%);
  }
  100% {
    transform: translateX(1);
  }
}

/* Store Information Section */
.store-image {
  /* width: 100%; - Removed to respect store-image-size classes */
  object-fit: cover;
  border-radius: 15px;
  transition: all 0.3s ease;
}

.store-image:hover {
  transform: translateY(-5px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
}

.store-description {
  padding: 0 0 0 1rem;
  text-align: left;
}

.store-title {
  font-family: var(--font-sans);
  font-size: 20px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1.5rem;
  line-height: 1.4;
  text-align: left;
}

.store-text {
  font-family: var(--font-sans);
  font-size: 16px;
  color: #34495e;
  line-height: 1.6;
  margin-bottom: 1rem;
  text-align: justify;
  white-space: pre-line;
}

.store-features {
  font-family: var(--font-sans);
  font-size: 16px;
  color: #34495e;
  line-height: 1.6;
  margin: 1.5rem 0;
  padding-left: 1.5rem;
}

.store-features li {
  margin-bottom: 0.75rem;
  position: relative;
}

.store-features li::before {
  content: '✓';
  position: absolute;
  left: -1.5rem;
  color: #9e7e00;
  font-weight: bold;
  font-size: 18px;
}

.store-conclusion {
  font-family: var(--font-sans);
  font-size: 16px;
  color: #34495e;
  line-height: 1.6;
  font-weight: 600;
  text-align: justify;
  border-left: 4px solid #9e7e00;
  padding-left: 1rem;
  margin-top: 1.5rem;
  white-space: pre-line;
  background: rgba(158, 126, 0, 0.05);
  padding: 0.5rem;
  border-radius: 0 8px 8px 0;
}

/* Featured Εκδρομές Section */
.featured-εκδρομές-section-wrapper {
  background: #f8f9fa;
  margin: 2rem 0;
}

.featured-εκδρομές-section {
  padding: 0.5rem 0;
}

/* Loading State */
.εκδρομές-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 0.75rem 0;
}

.loading-spinner {
  width: 50px;
  height: 50px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #3498db;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

.loading-text {
  margin-top: 1rem;
  color: #666;
  font-size: 1.1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Empty State */
.empty-products-state {
  padding: 1rem 0.5rem;
  text-align: center;
}

.empty-icon {
  font-size: 4rem;
  color: #bdc3c7;
  margin-bottom: 1.5rem;
}

.empty-title {
  font-family: var(--font-sans);
  font-size: 1.8rem;
  color: #2c3e50;
  margin-bottom: 1rem;
}

.empty-description {
  font-size: 1.1rem;
  color: #7f8c8d;
  margin-bottom: 2rem;
}

.featured-title {
  font-family: var(--font-sans);
  font-size: 28px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 2rem;
  position: relative;
}

.featured-title::after {
  content: '';
  position: absolute;
  bottom: -10px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: linear-gradient(45deg, #9e7e00, #b8940a);
  border-radius: 2px;
}

.εκδρομή-image-container {
  position: relative;
  width: 100%;
  height: 250px;
  overflow: hidden;
}

.εκδρομή-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.4s ease;
}

/* Product Badge */
.product-badge {
  position: absolute;
  top: 15px;
  left: 15px;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  color: white;
  padding: 0.2rem 0.4rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 3;
  box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
}

.badge-text {
  letter-spacing: 0.5px;
}

/* Product Actions Overlay */
.product-actions-overlay {
  position: absolute;
  top: 0;
  right: 0;
  height: 100%;
  width: 60px;
  background: linear-gradient(to left, rgba(0, 0, 0, 0.7), transparent);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  opacity: 0;
  transform: translateX(100%);
  transition: all 0.3s ease;
  z-index: 2;
}

.product-actions {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.action-btn {
  width: 40px;
  height: 40px;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.9);
  color: #2c3e50;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  backdrop-filter: blur(10px);
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.action-btn:hover {
  background: white;
  transform: scale(1.1);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.2);
}

.action-btn.quick-view-btn:hover {
  color: #3498db;
}

.action-btn.wishlist-btn:hover {
  color: #e74c3c;
}

.action-btn.wishlist-btn.active {
  background: #e74c3c;
  color: white;
}

.action-btn.cart-btn:hover {
  color: #27ae60;
}

/* Price Badge */
.price-badge {
  position: absolute;
  bottom: 15px;
  right: 15px;
  background: rgba(0, 0, 0, 0.8);
  color: white;
  padding: 0.25rem 0.5rem;
  border-radius: 25px;
  backdrop-filter: blur(10px);
  z-index: 3;
}

.current-price {
  font-weight: 700;
  font-size: 1.1rem;
}

.original-price {
  font-size: 0.9rem;
  text-decoration: line-through;
  color: #bdc3c7;
  margin-left: 0.5rem;
}

.product-info {
  padding: 0.75rem;
  text-align: center;
}

.product-name {
  font-family: var(--font-sans);
  font-size: 15px;
  font-weight: 600;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  cursor: pointer;
  transition: color 0.3s ease;
}

.product-name-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 0.5rem;
}

.product-name-row .category-icon {
  font-size: 14px;
  color: #3498db;
  margin-top: 2px;
  flex-shrink: 0;
}

.product-name-row .product-name {
  margin-bottom: 0;
  flex: 1;
}

.product-name:hover {
  color: #3498db;
}

.product-brand {
  font-family: var(--font-sans);
  font-size: 14px;
  color: #9e7e00;
  font-weight: 600;
  margin-bottom: 0.25rem;
}

.product-category {
  display: flex;
  align-items: center;
  gap: 5px;
  font-family: var(--font-sans);
  font-size: 12px;
  color: #7f8c8d;
  margin-bottom: 1rem;
  letter-spacing: 0.5px;
}

.product-category .bi {
  font-size: 12px;
  color: #3498db;
}

/* Αξιολόγηση Εκδρομής */
.product-rating {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.stars {
  display: flex;
  gap: 0.2rem;
}

.stars i {
  color: #f39c12;
  font-size: 0.9rem;
}

.rating-text {
  font-size: 0.85rem;
  color: #7f8c8d;
}

/* Quick Actions */
.product-quick-actions {
  margin-top: 1rem;
}

.add-to-cart-btn {
  width: 100%;
  border-radius: 25px;
  padding: 0.3rem 0.6rem;
  font-weight: 600;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  border: none;
  color: white;
  transition: all 0.3s ease;
  font-size: 0.9rem;
  box-shadow: 0 4px 15px rgba(231, 76, 60, 0.3);
}

.add-to-cart-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(231, 76, 60, 0.4);
  background: linear-gradient(135deg, #c0392b, #e74c3c);
  color: white;
}

/* View All Button */
.view-all-btn {
  border-radius: 25px;
  padding: 0.4rem 1rem;
  font-weight: 600;
  border: 2px solid #3498db;
  transition: all 0.3s ease;
  text-decoration: none;
}

.view-all-btn:hover {
  background: #3498db;
  color: white;
  transform: translateY(-3px);
  box-shadow: 0 10px 25px rgba(52, 152, 219, 0.3);
}

/* Responsive Design */
@media (max-width: 768px) {
  .home {
    padding: 0;
    margin: 0;
  }

  .home .container {
    max-width: 100%;
    padding-left: 0;
    padding-right: 0;
    margin: 0 auto;
    margin-top: 0 !important;
  }

  /* Gallery section - mobile responsive */
  .gallery-section {
    margin-top: 0 !important;
    padding-top: 0 !important;
    padding-bottom: 0.5rem !important;
  }

  /* Gallery modern variant */
  .gallery-modern {
    margin-top: 0 !important;
  }

  .main-image-container {
    height: 300px;
    margin: 0 auto;
  }

  .main-image-overlay {
    padding: 0.5rem;
  }

  .main-overlay-content h3 {
    font-size: 1.2rem;
  }

  .thumbnail-item {
    min-width: 80px;
    height: 80px;
  }

  .thumbnail-scroll {
    gap: 0.5rem;
    padding: 0.25rem 0;
  }

  .lightbox-image {
    max-width: 95vw;
    max-height: 60vh;
  }

  .lightbox-close {
    top: -40px;
    right: -10px;
    width: 40px;
    height: 40px;
    font-size: 1.2rem;
  }

  .lightbox-nav {
    width: 50px;
    height: 50px;
    font-size: 1.2rem;
  }

  .lightbox-prev {
    left: -60px;
  }

  .lightbox-next {
    right: -60px;
  }

  .lightbox-info {
    margin-top: 0.5rem;
    padding: 0 0.5rem;
  }

  .lightbox-info h4 {
    font-size: 1.2rem;
  }

  .lightbox-info p {
    font-size: 0.9rem;
  }

  /* Footer Responsive */
  .footer-section {
    padding: 0.5rem 0 !important;
  }

  .footer-content {
    padding: 0 0.5rem;
    text-align: center;
  }

  .company-name {
    font-size: 20px;
    letter-spacing: 0.5px;
  }

  .tagline {
    font-size: 18px;
    letter-spacing: 0.25px;
  }

  .motto {
    font-size: 18px;
    letter-spacing: 0.25px;
  }

  .golden-dots-line {
    padding: 0.25rem 0;
  }

  .dot {
    width: 3px;
    height: 3px;
  }

  /* Store Information Responsive */
  .store-image {
    height: 250px;
    margin-bottom: 2rem;
    width: 100%;
  }

  .store-description {
    padding: 0;
    text-align: left;
  }

  .store-title {
    font-size: 18px;
    text-align: center;
    margin-bottom: 1rem;
  }

  .store-text {
    font-size: 14px;
    text-align: left;
    white-space: pre-line;
  }

  .store-features {
    font-size: 14px;
    margin: 1rem 0;
  }

  .store-conclusion {
    font-size: 14px;
    text-align: left;
    margin-top: 1rem;
    white-space: pre-line;
  }

  /* Featured Εκδρομές Responsive */
  .featured-εκδρομές-section-wrapper {
    margin: 1rem 0;
    padding: 1rem 0 !important;
  }

  .featured-title {
    font-size: 24px;
  }

  @media (max-width: 576px) {
    .home {
      padding: 0 !important;
      margin: 0 !important;
      width: 100% !important;
    }

    .home .container {
      max-width: 100% !important;
      margin-top: 0 !important;
      padding-left: 8px !important;
      padding-right: 8px !important;
      margin: 0 auto !important;
    }

    /* Gallery section - mobile responsive smaller screens */
    .gallery-section {
      margin-top: 0 !important;
      padding-top: 0 !important;
      padding-bottom: 0.5rem !important;
    }

    .gallery-modern {
      margin-top: 0 !important;
    }

    .main-image-container {
      height: 250px;
      margin: 0 auto;
    }

    .thumbnail-item {
      min-width: 70px;
      height: 70px;
    }

    .gallery-item {
      margin-bottom: 1rem;
    }

    .lightbox-nav {
      display: none; /* Hide navigation on very small screens */
    }

    .lightbox-close {
      top: 10px;
      right: 10px;
      position: fixed;
    }

    /* Footer Mobile */
    .footer-section {
      padding: 0.5rem 0 !important;
    }

    .footer-content {
      text-align: center;
      padding: 0 0.5rem;
    }

    .company-name {
      font-size: 18px;
      text-align: center;
      letter-spacing: 0.5px;
    }

    .tagline {
      font-size: 16px;
      text-align: center;
      letter-spacing: 0.25px;
    }

    .motto {
      font-size: 16px;
      text-align: center;
      letter-spacing: 0.25px;
    }

    .golden-dots-line {
      padding: 0.25rem 0;
    }

    .dot {
      width: 2px;
      height: 2px;
    }

    /* Store Information Mobile */
    .store-image {
      height: 220px;
      margin-bottom: 1.5rem;
      width: 100%;
    }

    .store-description {
      padding: 0;
      text-align: left;
    }

    .store-title {
      font-size: 16px;
      text-align: center;
      margin-bottom: 1rem;
    }

    .store-text {
      font-size: 13px;
      text-align: left;
      white-space: pre-line;
    }

    .store-features {
      font-size: 13px;
      margin: 1rem 0;
      padding-left: 1rem;
    }

    .store-conclusion {
      font-size: 13px;
      text-align: left;
      margin-top: 1rem;
      padding: 0.75rem;
      white-space: pre-line;
    }

    /* Featured Εκδρομές Mobile */
    .featured-εκδρομές-section-wrapper {
      margin: 1rem 0;
      padding: 0.75rem 0 !important;
    }

    .featured-title {
      font-size: 20px;
      text-align: center;
    }
  }

  /* Extra small screens */
  @media (max-width: 480px) {
    .home {
      padding: 0 !important;
      margin: 0 !important;
      width: 100vw !important;
      overflow-x: hidden;
    }

    .home .container {
      max-width: 100% !important;
      padding-left: 5px !important;
      padding-right: 5px !important;
      margin: 0 auto !important;
      width: 100% !important;
    }

    /* Gallery section - extra small screens responsive */
    .gallery-section {
      margin-top: 0 !important;
      padding-top: 0 !important;
      padding-bottom: 0.5rem !important;
    }

    .gallery-modern {
      margin-top: 0 !important;
    }

    .main-image-container {
      height: 200px;
      border-radius: 8px;
    }

    .thumbnail-item {
      min-width: 60px;
      height: 60px;
    }

    .thumbnail-scroll {
      padding: 0.25rem 0;
      gap: 0.25rem;
    }

    .footer-content {
      padding: 0 0.25rem;
    }

    .company-name {
      font-size: 16px;
    }

    .tagline {
      font-size: 14px;
    }

    .motto {
      font-size: 14px;
    }

    .store-title {
      font-size: 14px;
    }

    .store-text,
    .store-features,
    .store-conclusion {
      font-size: 12px;
    }

    .store-text {
      white-space: pre-line;
    }
    .featured-title {
      font-size: 18px;
    }
  }
}

/* Custom scrollbar for webkit browsers */
::-webkit-scrollbar {
  width: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb {
  background: #3498db;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #2980b9;
}

/* Fallback animations for AOS */
[data-aos] {
  opacity: 1 !important;
  transform: translateY(0) !important;
  transition: all 0.8s ease-out;
}

[data-aos='fade-right'] {
  opacity: 1 !important;
  transform: translateX(0) !important;
}

[data-aos='fade-up'] {
  opacity: 1 !important;
  transform: translateY(0) !important;
}

/* Extra Sections Styling */
.extra-section {
  margin: 0;
  padding: 0;
  position: relative;
}

.extra-section:has(.gallery-widget),
.extra-section.gallery-widget-section {
  overflow: visible;
  margin: 0 !important;
  padding: 0 !important;
}

.extra-section.gallery-widget-section + .extra-section,
.extra-section:has(.gallery-widget) + .extra-section {
  margin-top: 1rem !important;
}

.extra-sections:has(.gallery-widget-section) + .extra-sections > .container-fluid > .extra-section:first-child {
  margin-top: 1rem !important;
}

/* Remove background from extra-section when it has transparent_background flag */
.extra-section.transparent-background {
  background: transparent !important;
}

.section-image {
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  transition: transform 0.3s ease;
}

.section-image:hover {
  transform: translateY(-5px);
}

.section-content {
  padding: 0.5rem;
}

/* Mobile optimizations */
@media (max-width: 768px) {
  /* Natural full-width layout like gallery-section */
  .extra-sections .container,
  .extra-sections .container-fluid {
    padding-left: 5px !important;
    padding-right: 5px !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
    max-width: 100% !important;
  }

  /* Natural column spacing */
  .extra-sections .row > [class*='col-'] {
    padding-left: 5px !important;
    padding-right: 5px !important;
  }

  /* Remove section padding on mobile */
  .extra-section {
    padding: 0 !important;
  }

  /* Clean section-content - no extra constraints */
  .section-content {
    padding: 0 !important;
  }

  /* Hide sections marked as hidden_on_mobile */
  .extra-section.hidden-on-mobile {
    display: none !important;
  }
}

.section-title {
  color: var(--primary-gold);
  font-weight: 700;
  margin-bottom: 1.5rem;
  font-size: 1.8rem;
}

.section-text {
  color: #555;
  line-height: 1.8;
  font-size: 1.1rem;
}

.section-text p {
  margin-bottom: 1rem;
}

/* Enhanced Section Gallery Styles */
.section-gallery-grid {
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
}

.section-gallery-image {
  height: 200px;
  object-fit: cover;
  transition:
    transform 0.3s ease,
    filter 0.3s ease;
  cursor: pointer;
}

.section-gallery-image:hover {
  transform: scale(1.05);
  filter: brightness(1.1);
}

.section-gallery-carousel {
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.section-carousel-image {
  height: 400px;
  object-fit: cover;
}

.section-single-image {
  position: relative;
  overflow: hidden;
  border-radius: 15px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
}

.section-single-image .section-image {
  cursor: pointer;
  transition:
    transform 0.3s ease,
    filter 0.3s ease;
}

.section-single-image .section-image:hover {
  transform: scale(1.02);
  filter: brightness(1.1);
}

@media (max-width: 768px) {
  .extra-section {
    margin: 0.5rem 0;
  }

  .section-title {
    font-size: 1.5rem;
    margin-bottom: 1rem;
  }

  .section-text {
    font-size: 1rem;
  }

  .section-gallery-image {
    height: 150px;
  }

  .section-carousel-image {
    height: 250px;
  }
}

/* Grid Gallery Styles */
.gallery-grid .grid-image-item,
.grid-image-item {
  position: relative;
  overflow: hidden;
  border-radius: 12px;
  cursor: pointer;
  transition: transform 0.3s ease;
  height: 250px;
}

.gallery-grid .grid-image-item:hover,
.grid-image-item:hover {
  transform: translateY(-5px);
}

.gallery-grid .grid-image,
.grid-image {
  width: 100%;
  height: 250px;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.gallery-grid .grid-image-item:hover .grid-image,
.grid-image-item:hover .grid-image {
  transform: scale(1.1);
}

.gallery-grid .grid-overlay,
.grid-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(45deg, rgba(59, 130, 246, 0.8), rgba(10, 69, 99, 0.8));
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.gallery-grid .grid-image-item:hover .grid-overlay,
.grid-image-item:hover .grid-overlay {
  opacity: 1;
}

.gallery-grid .grid-content,
.grid-content {
  text-align: center;
}

.gallery-grid .grid-content h6,
.grid-content h6 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.gallery-grid .grid-content i,
.grid-content i {
  font-size: 1.5rem;
}

/* Carousel Gallery Styles */
.gallery-carousel .carousel-gallery,
.carousel-gallery {
  position: relative;
  height: 500px;
  overflow: hidden;
  border-radius: 20px;
}

.gallery-carousel .carousel-container,
.carousel-container {
  position: relative;
  width: 100%;
  height: 100%;
}

.gallery-carousel .carousel-slide,
.carousel-slide {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.5s ease-in-out;
}

.gallery-carousel .carousel-slide.active,
.carousel-slide.active {
  opacity: 1;
}

.gallery-carousel .carousel-image,
.carousel-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.gallery-carousel .carousel-content,
.carousel-content {
  position: absolute;
  bottom: 2rem;
  left: 2rem;
  right: 2rem;
  text-align: center;
  background: rgba(0, 0, 0, 0.5);
  padding: 0.75rem;
  border-radius: 12px;
  backdrop-filter: blur(10px);
}

.gallery-carousel .carousel-content h4,
.carousel-content h4 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.gallery-carousel .carousel-prev,
.gallery-carousel .carousel-next,
.carousel-prev,
.carousel-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
}

.gallery-carousel .carousel-prev,
.carousel-prev {
  left: 1rem;
}

.gallery-carousel .carousel-next,
.carousel-next {
  right: 1rem;
}

.gallery-carousel .carousel-prev:hover,
.gallery-carousel .carousel-next:hover,
.carousel-prev:hover,
.carousel-next:hover {
  background: white;
  transform: translateY(-50%) scale(1.1);
}

.gallery-carousel .carousel-dots,
.carousel-dots {
  position: absolute;
  bottom: 1rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.5rem;
  z-index: 10;
}

.gallery-carousel .carousel-dots .dot,
.carousel-dots .dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.5);
  border: none;
  cursor: pointer;
  transition: background-color 0.3s ease;
}

.gallery-carousel .carousel-dots .dot.active,
.gallery-carousel .carousel-dots .dot:hover,
.carousel-dots .dot.active,
.carousel-dots .dot:hover {
  background: white;
}

@media (max-width: 768px) {
  .gallery-carousel .carousel-gallery {
    height: 300px;
  }

  .gallery-carousel .carousel-content {
    bottom: 1rem;
    left: 1rem;
    right: 1rem;
    padding: 0.5rem;
  }

  .gallery-carousel .carousel-prev,
  .gallery-carousel .carousel-next {
    width: 40px;
    height: 40px;
  }

  .gallery-grid .grid-image {
    height: 200px;
  }
}

/* Masonry Gallery Styles */
.gallery-masonry {
  height: 500px;
  overflow-y: auto;
  padding: 10px;
  background: #f8f9fa;
  border-radius: 12px;
  border: 1px solid #e9ecef;
  margin: 2rem 0;
}

.masonry-gallery {
  margin: 2rem 0;
}

.masonry-grid {
  column-count: 3;
  column-gap: 1rem;
  margin: 0;
}

.masonry-item {
  display: inline-block;
  width: 100%;
  margin-bottom: 1rem;
  break-inside: avoid;
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition:
    transform 0.3s ease,
    box-shadow 0.3s ease;
}

.masonry-item:hover {
  transform: translateY(-5px);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
}

.masonry-image {
  width: 100%;
  height: auto;
  display: block;
  border-radius: 12px;
}

.masonry-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 0.5rem;
  transform: translateY(100%);
  transition: transform 0.3s ease;
}

.masonry-item:hover .masonry-overlay {
  transform: translateY(0);
}

.masonry-content h5 {
  margin-bottom: 0.5rem;
  font-weight: 600;
}

/* Responsive Masonry - Better mobile layout */
@media (max-width: 768px) {
  .masonry-grid {
    column-count: 2;
    column-gap: 0.75rem;
  }

  .masonry-item {
    margin-bottom: 0.75rem;
  }

  .masonry-overlay {
    padding: 0.375rem;
  }
}

@media (max-width: 480px) {
  .masonry-grid {
    column-count: 1;
    column-gap: 0;
  }

  .masonry-item {
    margin-bottom: 1rem;
    border-radius: 8px;
  }

  .masonry-image {
    border-radius: 8px;
  }

  .masonry-overlay {
    padding: 1rem;
  }

  .masonry-content h5 {
    font-size: 1rem;
  }

  .masonry-content p {
    font-size: 0.9rem;
  }
}

/* Slider Gallery Styles */
.slider-gallery {
  margin: 2rem 0;
}

.slider-container {
  position: relative;
  width: 100%;
  height: 600px;
  overflow: hidden;
  border-radius: 16px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
}

.slider-track {
  display: flex;
  width: 100%;
  height: 100%;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.slider-slide {
  min-width: 100%;
  height: 100%;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
}

.slider-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  cursor: pointer;
}

.slider-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.8), transparent);
  padding: 1rem;
  transform: translateY(100%);
  transition: transform 0.4s ease;
}

.slider-slide:hover .slider-overlay {
  transform: translateY(0);
}

.slider-content h3 {
  font-size: 1.5rem;
  margin-bottom: 0.5rem;
  font-weight: 600;
}

.slider-prev,
.slider-next {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(255, 255, 255, 0.9);
  border: none;
  border-radius: 50%;
  width: 60px;
  height: 60px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 10;
  font-size: 1.2rem;
}

.slider-prev {
  left: 2rem;
}

.slider-next {
  right: 2rem;
}

.slider-prev:hover,
.slider-next:hover {
  background: white;
  transform: translateY(-50%) scale(1.1);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.2);
}

.slider-indicators {
  position: absolute;
  bottom: 2rem;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 0.75rem;
  z-index: 10;
}

.slider-indicator {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  border: none;
  background: rgba(255, 255, 255, 0.5);
  cursor: pointer;
  transition: all 0.3s ease;
}

.slider-indicator.active {
  background: white;
  transform: scale(1.3);
  box-shadow: 0 0 15px rgba(255, 255, 255, 0.8);
}

.slider-indicator:hover {
  background: rgba(255, 255, 255, 0.8);
}

/* Responsive Slider */
@media (max-width: 768px) {
  .slider-container {
    height: 400px;
  }

  .slider-prev,
  .slider-next {
    width: 50px;
    height: 50px;
    font-size: 1rem;
  }

  .slider-prev {
    left: 1rem;
  }

  .slider-next {
    right: 1rem;
  }

  .slider-overlay {
    padding: 0.75rem;
  }
}

@media (max-width: 480px) {
  .slider-container {
    height: 300px;
  }

  .slider-prev,
  .slider-next {
    width: 40px;
    height: 40px;
  }

  .slider-overlay {
    padding: 0.5rem;
  }

  .slider-content h3 {
    font-size: 1.2rem;
  }
}

/* 🎨 MODERN ENHANCEMENTS */

/* Hero Section with Gradient Overlay */
.home {
  position: relative;
  overflow-x: hidden;
  min-height: 100vh;
}

.home > * {
  position: relative;
  z-index: 1;
}

/* Enhanced Header with Glass Effect */
.header-content {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 25px;
  padding: 1.5rem 1.25rem;
  margin: 2rem 0 4rem 0;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.2);
  position: relative;
  overflow: hidden;
  transform: translateY(20px);
  animation: slideInUp 1s ease-out forwards;
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
}

.header-content * {
  max-width: 100%;
  word-break: break-word;
  overflow-wrap: break-word;
}

.header-content::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: conic-gradient(from 0deg, transparent, rgba(212, 175, 55, 0.1), transparent);
  animation: rotate 20s linear infinite;
  z-index: -1;
}

@keyframes slideInUp {
  to {
    transform: translateY(0);
  }
}

@keyframes rotate {
  to {
    transform: rotate(360deg);
  }
}

/* Enhanced Typography with Gradient Text */
.company-name {
  background: linear-gradient(
    135deg,
    var(--primary-gold) 0%,
    #f39c12 50%,
    var(--primary-gold) 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-weight: 800;
  text-shadow: none;
  position: relative;
  display: inline-block;
  animation: shimmer 3s ease-in-out infinite;
  font-size: clamp(1.8rem, 4vw, 3.5rem);
  word-break: break-word;
  overflow-wrap: break-word;
  max-width: 100%;
}

@keyframes shimmer {
  0%,
  100% {
    filter: brightness(1);
  }
  50% {
    filter: brightness(1.2);
  }
}

.tagline {
  color: #2c3e50;
  font-weight: 600;
  letter-spacing: 0.5px;
  opacity: 0;
  animation: fadeInDelay 1s ease-out 0.5s forwards;
}

.motto {
  background: linear-gradient(45deg, #0C5A85 0%, #0a4563 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  font-style: italic;
  opacity: 0;
  animation: fadeInDelay 1s ease-out 1s forwards;
}

@keyframes fadeInDelay {
  to {
    opacity: 1;
  }
}

/* Enhanced Sections - Minimal base styles, settings come from admin */
.extra-section:not(:has(.gallery-widget)):not(.gallery-widget-section),
.store-section,
.featured-εκδρομές-section {
  position: relative;
  overflow: hidden;
}

/* Enhanced Section Titles */
.section-title,
.store-title,
.featured-title {
  position: relative;
  display: inline-block;
  margin-bottom: 2.5rem;
}

.section-title::after,
.store-title::after,
.featured-title::after {
  content: '';
  position: absolute;
  bottom: -15px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 4px;
  background: linear-gradient(90deg, var(--primary-gold), #f39c12, var(--primary-gold));
  border-radius: 2px;
  animation: expandWidth 1s ease-out;
}

@keyframes expandWidth {
  from {
    width: 0;
  }
  to {
    width: 80px;
  }
}

/* Enhanced Product Cards */
.εκδρομή-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(10px);
  border-radius: 25px;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow:
    0 15px 35px rgba(0, 0, 0, 0.1),
    0 5px 15px rgba(0, 0, 0, 0.05);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  overflow: hidden;
  position: relative;
}

.εκδρομή-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow:
    0 25px 50px rgba(0, 0, 0, 0.15),
    0 10px 25px rgba(0, 0, 0, 0.1);
}

.εκδρομή-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
  transition: left 0.6s ease;
}

.εκδρομή-card:hover::before {
  left: 100%;
}

/* Enhanced Gallery with 3D Effects */
.gallery-section {
  perspective: 1000px;
}

.main-image-container {
  transform-style: preserve-3d;
  transition: transform 0.6s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 20px 40px rgba(0, 0, 0, 0.15),
    0 0 0 1px rgba(255, 255, 255, 0.2);
}

.main-image-container:hover {
  transform: rotateY(5deg) rotateX(5deg) translateZ(50px);
}

.main-image {
  transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
}

.main-image:hover {
  transform: scale(1.05);
  filter: brightness(1.1) contrast(1.1);
}

/* Enhanced Thumbnails */
.thumbnail-item {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 15px;
  overflow: hidden;
  position: relative;
}

.thumbnail-item::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background: linear-gradient(45deg, transparent, rgba(212, 175, 55, 0.3));
  opacity: 0;
  transition: opacity 0.3s ease;
}

.thumbnail-item:hover::after {
  opacity: 1;
}

.thumbnail-item.active {
  transform: scale(1.1);
  box-shadow: 0 10px 25px rgba(212, 175, 55, 0.4);
}

/* Enhanced Buttons */
.add-to-cart-btn,
.btn-primary {
  background: linear-gradient(135deg, #e74c3c 0%, #c0392b 100%);
  border: none;
  border-radius: 25px;
  padding: 6px 15px;
  font-weight: 600;
  letter-spacing: 1px;
  position: relative;
  overflow: hidden;
  color: white;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 25px rgba(231, 76, 60, 0.3);
}

.add-to-cart-btn::before,
.btn-primary::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 50%;
  transform: translate(-50%, -50%);
  transition: all 0.6s ease;
}

.add-to-cart-btn:hover::before,
.btn-primary:hover::before {
  width: 300px;
  height: 300px;
}

.add-to-cart-btn:hover,
.btn-primary:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(231, 76, 60, 0.4);
  color: white;
}

/* Loading Animation Enhancement */
.loading-spinner {
  width: 60px;
  height: 60px;
  border: 4px solid rgba(212, 175, 55, 0.2);
  border-top: 4px solid var(--primary-gold);
  border-radius: 50%;
  animation: modernSpin 1s linear infinite;
  margin: 0 auto 1rem auto;
  position: relative;
}

.loading-spinner::after {
  content: '';
  position: absolute;
  inset: 8px;
  border: 2px solid transparent;
  border-top: 2px solid #f39c12;
  border-radius: 50%;
  animation: modernSpin 0.5s linear infinite reverse;
}

@keyframes modernSpin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

/* Enhanced Scroll Animations */
[data-aos] {
  transition-duration: 1s;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
}

/* Improved Mobile Experience */
@media (max-width: 768px) {
  .header-content {
    padding: 1rem 0.75rem;
    border-radius: 20px;
    margin: 1rem 0 2rem 0;
    max-width: 100%;
  }

  .header-content * {
    word-break: break-word !important;
    overflow-wrap: break-word !important;
    max-width: 100% !important;
  }

  .extra-section,
  .store-section,
  .featured-εκδρομές-section {
    padding: 1.25rem 0.75rem;
    border-radius: 20px;
    margin: 2rem 0;
  }

  /* Override for post cards and gallery widgets - remove all padding */
  .extra-section:has(.post-cards-widget),
  .extra-section:has(.gallery-widget),
  .extra-section.gallery-widget-section {
    padding: 0 !important;
    margin: 0 !important;
  }

  .extra-section.gallery-widget-section + .extra-section,
  .extra-section:has(.gallery-widget) + .extra-section {
    margin-top: 0.75rem !important;
  }

  .main-image-container:hover {
    transform: none;
  }

  .εκδρομή-card:hover {
    transform: translateY(-4px);
  }
}

/* Dark Mode Support */
@media (prefers-color-scheme: dark) {
  .home {
    background: linear-gradient(135deg, #1a1a2e 0%, #16213e 100%);
  }

  .header-content,
  .extra-section,
  .store-section,
  .featured-εκδρομές-section {
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }

  .εκδρομή-card {
    background: rgba(255, 255, 255, 0.08);
    border: 1px solid rgba(255, 255, 255, 0.1);
  }
}

/* Performance Optimizations */
.εκδρομή-card,
.main-image-container,
.thumbnail-item {
  will-change: transform;
}

.εκδρομή-card:not(:hover),
.main-image-container:not(:hover),
.thumbnail-item:not(:hover) {
  will-change: auto;
}

/* Optical Masonry Gallery Styles */
.optical-masonry-section {
  background: linear-gradient(
    135deg,
    rgba(255, 107, 107, 0.9) 0%,
    rgba(255, 159, 67, 0.9) 25%,
    rgba(255, 221, 87, 0.9) 50%,
    rgba(72, 219, 251, 0.9) 75%,
    rgba(116, 185, 255, 0.9) 100%
  );
  position: relative;
  overflow: hidden;
}

.optical-masonry-section::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background:
    radial-gradient(circle at 20% 20%, rgba(255, 99, 132, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 80% 80%, rgba(54, 162, 235, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 40% 60%, rgba(255, 206, 84, 0.3) 0%, transparent 50%),
    radial-gradient(circle at 60% 40%, rgba(75, 192, 192, 0.3) 0%, transparent 50%);
  animation: backgroundFloat 20s ease-in-out infinite;
}

.masonry-header {
  position: relative;
  z-index: 2;
}

.masonry-title {
  font-size: 3rem;
  font-weight: 800;
  margin-bottom: 1rem;
  background: linear-gradient(
    45deg,
    #ff6b6b 0%,
    #4ecdc4 25%,
    #45b7d1 50%,
    #96ceb4 75%,
    #feca57 100%
  );
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.masonry-subtitle {
  font-size: 1.2rem;
  color: rgba(255, 255, 255, 0.95);
  max-width: 600px;
  margin: 0 auto;
  text-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
  white-space: pre-line;
}

.optical-masonry-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  grid-auto-rows: 200px;
  gap: 20px;
  position: relative;
  z-index: 2;
}

.masonry-item {
  border-radius: 20px;
  overflow: hidden;
  position: relative;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.masonry-item.masonry-tall {
  grid-row: span 2;
}

.masonry-item.masonry-wide {
  grid-column: span 2;
}

.masonry-item.masonry-square {
  grid-row: span 1;
}

.masonry-item.masonry-regular {
  grid-row: span 1;
}

.masonry-card {
  width: 100%;
  height: 100%;
  position: relative;
  border-radius: 20px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.25);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.4);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.2);
}

.masonry-card:hover {
  transform: translateY(-15px) scale(1.03);
  box-shadow:
    0 40px 80px rgba(0, 0, 0, 0.25),
    0 0 60px rgba(255, 159, 67, 0.4);
  border-color: rgba(255, 159, 67, 0.8);
  background: rgba(255, 255, 255, 0.35);
}

.masonry-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.masonry-card:hover .masonry-image {
  transform: scale(1.1);
  filter: brightness(1.1) contrast(1.1);
}

.masonry-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    180deg,
    transparent 0%,
    rgba(255, 107, 107, 0.3) 30%,
    rgba(255, 159, 67, 0.6) 70%,
    rgba(255, 206, 84, 0.8) 100%
  );
  display: flex;
  align-items: flex-end;
  padding: 12.5px;
  opacity: 0;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.masonry-card:hover .masonry-overlay {
  opacity: 1;
}

.masonry-content {
  width: 100%;
  transform: translateY(20px);
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
}

.masonry-card:hover .masonry-content {
  transform: translateY(0);
}

.masonry-tag {
  display: inline-block;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
  padding: 6px 10px;
  border-radius: 30px;
  font-size: 0.9rem;
  font-weight: 700;
  letter-spacing: 1.2px;
  margin-bottom: 12px;
  box-shadow: 0 10px 30px rgba(59, 130, 246, 0.4);
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
  border: 2px solid rgba(255, 255, 255, 0.4);
  transition: all 0.3s ease;
}

.masonry-tag:hover {
  transform: translateY(-2px);
  box-shadow: 0 15px 40px rgba(59, 130, 246, 0.6);
}

.masonry-title-card {
  color: white;
  font-size: 1.4rem;
  font-weight: 700;
  margin-bottom: 8px;
  line-height: 1.3;
  text-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

.masonry-description {
  color: rgba(255, 255, 255, 0.95);
  font-size: 0.95rem;
  line-height: 1.4;
  margin: 0;
  text-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
}

/* Colorful Tag Variations */
.tag-sunglasses {
  background: linear-gradient(135deg, #ffeaa7 0%, #fab1a0 100%) !important;
  box-shadow: 0 10px 30px rgba(255, 234, 167, 0.5) !important;
}

.tag-reading {
  background: linear-gradient(135deg, #81ecec 0%, #74b9ff 100%) !important;
  box-shadow: 0 10px 30px rgba(129, 236, 236, 0.5) !important;
}

.tag-designer {
  background: linear-gradient(135deg, #fd79a8 0%, #a29bfe 100%) !important;
  box-shadow: 0 10px 30px rgba(253, 121, 168, 0.5) !important;
}

.tag-sport {
  background: linear-gradient(135deg, #00b894 0%, #00cec9 100%) !important;
  box-shadow: 0 10px 30px rgba(0, 184, 148, 0.5) !important;
}

.tag-professional {
  background: linear-gradient(135deg, #636e72 0%, #2d3436 100%) !important;
  box-shadow: 0 10px 30px rgba(99, 110, 114, 0.5) !important;
}

.tag-fashion {
  background: linear-gradient(135deg, #e17055 0%, #d63031 100%) !important;
  box-shadow: 0 10px 30px rgba(225, 112, 85, 0.5) !important;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .optical-masonry-section {
    padding: 1rem 0 !important; /* Add padding for mobile */
  }

  .optical-masonry-grid {
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    grid-auto-rows: 160px;
    gap: 12px;
    padding: 0 10px;
  }

  .masonry-item.masonry-wide {
    grid-column: span 1; /* Force single column on mobile */
  }

  .masonry-item.masonry-tall {
    grid-row: span 2; /* Keep tall items but adjust for mobile */
  }

  .masonry-title {
    font-size: 2.2rem;
  }

  .masonry-subtitle {
    font-size: 1rem;
    padding: 0 7.5px;
    white-space: pre-line;
  }

  .masonry-overlay {
    padding: 7.5px;
  }

  .masonry-title-card {
    font-size: 1.1rem;
  }

  .masonry-description {
    font-size: 0.85rem;
  }

  .masonry-tag {
    padding: 4px 7.5px;
    font-size: 0.8rem;
    letter-spacing: 0.8px;
  }
}

@media (max-width: 480px) {
  .optical-masonry-grid {
    grid-template-columns: 1fr 1fr; /* Two columns on very small screens */
    grid-auto-rows: 140px;
    gap: 10px;
    padding: 0 5px;
  }

  .masonry-item.masonry-tall {
    grid-row: span 1; /* Single row height on very small screens */
  }

  .masonry-item.masonry-wide {
    grid-column: span 2; /* Span full width occasionally */
  }

  .masonry-title {
    font-size: 1.8rem;
  }

  .masonry-subtitle {
    font-size: 0.9rem;
    white-space: pre-line;
  }

  .masonry-overlay {
    padding: 6px;
  }

  .masonry-title-card {
    font-size: 1rem;
  }

  .masonry-description {
    font-size: 0.8rem;
    line-height: 1.3;
  }

  .masonry-tag {
    padding: 3px 6px;
    font-size: 0.75rem;
    margin-bottom: 8px;
  }
}

/* Animation Keyframes */
@keyframes backgroundFloat {
  0%,
  100% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
}

/* Quick View Modal Styles */
.quick-view-modal {
  border: none;
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.quick-view-modal .modal-header {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  color: white;
  padding: 0.75rem 1rem;
  border-bottom: none;
}

.quick-view-modal .modal-title {
  font-weight: 600;
  font-size: 1.25rem;
}

.quick-view-modal .btn-close {
  filter: invert(1);
  opacity: 0.8;
}

.quick-view-modal .btn-close:hover {
  opacity: 1;
}

.quick-view-image {
  position: relative;
  height: 400px;
  overflow: hidden;
}

.quick-view-image img {
  object-fit: cover;
  transition: transform 0.3s ease;
}

.quick-view-image:hover img {
  transform: scale(1.05);
}

.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.1) 0%, transparent 50%);
}

.quick-view-details {
  height: 400px;
  overflow-y: auto;
  background: #fafafa;
}

.quick-view-details .product-title {
  color: #2c3e50;
  font-weight: 700;
  line-height: 1.3;
}

.info-item {
  padding: 0.5rem 0;
  border-bottom: 1px solid #eee;
}

.info-item:last-child {
  border-bottom: none;
}

.info-item i {
  width: 20px;
  text-align: center;
}

.current-price {
  font-weight: 700;
  color: #007bff !important;
}

.action-buttons .btn-primary {
  background: linear-gradient(135deg, #007bff 0%, #0056b3 100%);
  border: none;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.action-buttons .btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 25px rgba(0, 123, 255, 0.3);
}

.action-buttons .btn-outline-primary {
  border: 2px solid #007bff;
  font-weight: 600;
  padding: 0.75rem 1.5rem;
  border-radius: 10px;
  transition: all 0.3s ease;
}

.action-buttons .btn-outline-primary:hover {
  background: #007bff;
  transform: translateY(-1px);
  box-shadow: 0 5px 15px rgba(0, 123, 255, 0.3);
}

.social-share .btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
}

.social-share .btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .quick-view-image,
  .quick-view-details {
    height: auto;
  }

  .quick-view-details {
    min-height: 300px;
  }
}

/* Εκδρομή Card Styles */
.εκδρομή-card {
  background: linear-gradient(145deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.06),
    0 4px 12px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  height: 100%;
  min-height: 350px !important;
  max-height: 420px !important;
  width: 100% !important;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  contain: layout !important;
  box-sizing: border-box !important;
  transform: none !important;
}

.εκδρομή-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(10, 69, 99, 0.03) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.εκδρομή-card:hover::before {
  opacity: 1;
}

.εκδρομή-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow:
    0 35px 60px rgba(0, 0, 0, 0.15),
    0 15px 35px rgba(59, 130, 246, 0.1);
}

.εκδρομή-image {
  position: relative;
  height: 220px;
  overflow: hidden;
  background: linear-gradient(45deg, #f8f9fa, #ffffff);
}

.product-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.4s ease;
  filter: brightness(1) saturate(1);
}

.εκδρομή-card:hover .product-img {
  transform: scale(1.1) rotate(1deg);
  filter: brightness(1.1) saturate(1.2);
}

.product-badges {
  position: absolute;
  top: 8px;
  left: 8px;
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  z-index: 3;
}

.badge {
  padding: 0.15rem 0.35rem;
  border-radius: 8px;
  font-size: 0.55rem;
  font-weight: 600;
  letter-spacing: 0.2px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(8px);
}

.featured-badge {
  background: linear-gradient(135deg, #ff6b6b, #ff8e8e);
  color: white;
  animation: featuredPulse 2s ease-in-out infinite;
}

@keyframes featuredPulse {
  0%,
  100% {
    box-shadow: 0 1px 4px rgba(255, 107, 107, 0.2);
  }
  50% {
    box-shadow: 0 2px 6px rgba(255, 107, 107, 0.4);
  }
}

.sale-badge {
  background: linear-gradient(135deg, #28a745, #20c997);
  color: white;
  animation: saleBounce 1s ease-in-out infinite alternate;
}

@keyframes saleBounce {
  from {
    transform: scale(1);
  }
  to {
    transform: scale(1.05);
  }
}

.product-info {
  padding: 0.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
}

.product-brand {
  font-size: 0.8rem;
  color: #3b82f6;
  letter-spacing: 1px;
  margin-bottom: 0.5rem;
  font-weight: 700;
  opacity: 0.8;
}

.product-name {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 2.6rem;
}

.εκδρομή-card:hover .product-name {
  color: #3b82f6;
}

.product-category {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 1rem;
  font-weight: 500;
}

.product-description {
  font-size: 0.85rem;
  color: #6c757d;
  line-height: 1.4;
  margin-bottom: 1rem;
}

.product-pricing {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-bottom: 1rem;
}

.sale-price {
  font-size: 1.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #e74c3c, #c0392b);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.current-price {
  font-size: 1rem;
  color: #2c3e50;
  font-weight: 700;
}

.original-price {
  text-decoration: line-through;
  color: #95a5a6;
  font-weight: 500;
  font-size: 1rem;
}

.εκδρομή-card-actions {
  margin-top: auto;
  padding-top: 1rem;
}

.book-btn {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  border: none;
  color: white;
  font-weight: 700;
  padding: 0.75rem 1.25rem;
  border-radius: 12px;
  transition: all 0.3s ease;
  letter-spacing: 1px;
  font-size: 0.8rem;
  position: relative;
  overflow: hidden;
}

.book-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: all 0.6s ease;
}

.book-btn:hover::before {
  left: 100%;
}

.book-btn:hover {
  transform: translateY(-3px);
  box-shadow: 0 15px 35px rgba(59, 130, 246, 0.4);
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
}

.product-actions-overlay {
  position: absolute;
  top: 20px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  opacity: 1;
  transition: all 0.3s ease;
  z-index: 3;
}

.action-btn {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.1rem;
  transition: all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  cursor: pointer;
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
}

.action-btn::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.1));
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.action-btn:hover::before {
  opacity: 1;
}

.view-btn {
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  color: white;
}

.view-btn:hover {
  transform: scale(1.15) rotate(5deg);
  box-shadow: 0 12px 35px rgba(59, 130, 246, 0.5);
}

.wishlist-btn {
  background: rgba(255, 255, 255, 0.9);
  color: #dc143c;
  border: 2px solid rgba(220, 20, 60, 0.5);
  transition: all 0.3s ease;
}

.wishlist-btn:hover {
  background: rgba(255, 255, 255, 1);
  border-color: #dc143c;
  transform: scale(1.05);
  box-shadow: 0 8px 20px rgba(220, 20, 60, 0.3);
}

.wishlist-btn.active-wishlist {
  background: linear-gradient(135deg, #dc143c 0%, #b71c1c 100%) !important;
  color: white !important;
  border: 2px solid #dc143c !important;
  animation: heartBeat 1s ease-in-out infinite;
  box-shadow: 0 8px 25px rgba(220, 20, 60, 0.4);
}

@keyframes heartBeat {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 8px 25px rgba(220, 20, 60, 0.4);
  }
  50% {
    transform: scale(1.1);
    box-shadow: 0 12px 35px rgba(220, 20, 60, 0.6);
  }
}

.wishlist-btn.active-wishlist:hover {
  background: linear-gradient(135deg, #b71c1c 0%, #8b0000 100%) !important;
  color: white !important;
  border-color: #b71c1c !important;
  box-shadow: 0 15px 40px rgba(139, 0, 0, 0.7);
  animation: none;
  transform: scale(1.15) rotate(-5deg);
}

/* Post Overlay for buttons */
.post-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.1) 0%, rgba(0, 0, 0, 0.3) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;
  z-index: 2;
}

.post-card:hover .post-overlay {
  opacity: 1;
}

.post-overlay > * {
  pointer-events: all;
}

.post-category {
  position: absolute;
  top: 15px;
  left: 15px;
  z-index: 3;
}

.post-featured {
  position: absolute;
  top: 15px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 3;
}

/* Post Wishlist Button Specific Styles */
.post-wishlist-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 40px;
  height: 40px;
  background: rgba(255, 255, 255, 0.95);
  color: #dc143c;
  border: none;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  font-size: 16px;
  z-index: 5;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
}

.post-wishlist-btn:hover {
  background: rgba(255, 255, 255, 1);
  transform: scale(1.1);
  color: #dc143c;
  box-shadow: 0 6px 20px rgba(220, 20, 60, 0.3);
}

.post-wishlist-btn.active,
.post-wishlist-btn.active-wishlist {
  background: linear-gradient(135deg, #dc143c 0%, #b71c1c 100%);
  color: white;
  animation: heartBeat 0.6s ease-in-out;
}

.post-wishlist-btn.active:hover,
.post-wishlist-btn.active-wishlist:hover {
  background: linear-gradient(135deg, #b71c1c 0%, #8b0000 100%);
  color: white;
  transform: scale(1.15);
}

/* Responsive Design */
@media (max-width: 768px) {
  .εκδρομή-card {
    min-height: 300px !important;
    max-height: 350px !important;
  }

  .εκδρομή-image {
    height: 180px;
  }

  .product-info {
    padding: 0.5rem;
  }

  .product-name {
    font-size: 0.85rem;
  }

  .action-btn {
    width: 35px;
    height: 35px;
    font-size: 1rem;
  }
}

/* Extra Sections - Products & Products Styles */
.section-products,
.section-products {
  width: 100%;
}

.products-grid,
.products-grid {
  margin-top: 1rem;
}

.product-card,
.product-card {
  background: linear-gradient(145deg, #ffffff 0%, #fafbfc 100%);
  border-radius: 20px;
  overflow: hidden;
  box-shadow:
    0 12px 28px rgba(0, 0, 0, 0.06),
    0 4px 12px rgba(0, 0, 0, 0.03);
  transition: box-shadow 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275);
  cursor: pointer;
  height: 100%;
  min-height: 350px;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
}

.product-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(59, 130, 246, 0.03) 0%, rgba(10, 69, 99, 0.03) 100%);
  opacity: 0;
  transition: opacity 0.3s ease;
  z-index: 1;
}

.product-card:hover::before {
  opacity: 1;
}

.product-card:hover,
.product-card:hover {
  transform: translateY(-15px) scale(1.02);
  box-shadow:
    0 35px 60px rgba(0, 0, 0, 0.15),
    0 15px 35px rgba(59, 130, 246, 0.1);
}

.product-image,
.product-image {
  position: relative;
  overflow: hidden;
  height: 220px;
  flex-shrink: 0;
}

.product-image img,
.product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.product-card:hover .product-image img,
.product-card:hover .product-image img {
  transform: scale(1.08);
}

/* Gallery Thumbnails for Home.vue */
.gallery-thumbnails {
  position: relative;
  display: flex;
  gap: 4px;
  margin-top: 8px;
  padding: 0 8px;
  z-index: 2;
}

.gallery-thumb {
  flex: 1;
  height: 35px;
  border-radius: 6px;
  overflow: hidden;
  position: relative;
  border: 2px solid rgba(212, 175, 55, 0.3);
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
}

.gallery-thumb:hover {
  transform: translateY(-2px);
  border-color: #d4af37;
  box-shadow: 0 4px 12px rgba(212, 175, 55, 0.4);
}

.gallery-thumb-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: all 0.3s ease;
}

.gallery-thumb:hover .gallery-thumb-img {
  transform: scale(1.1);
}

.gallery-thumb.has-more {
  background: rgba(0, 0, 0, 0.6);
}

.gallery-thumb.has-more .gallery-thumb-img {
  opacity: 0.5;
}

.more-indicator {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: white;
  font-size: 0.65rem;
  font-weight: 700;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.5);
  z-index: 3;
  pointer-events: none;
}

.product-overlay,
.product-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.product-card:hover .product-overlay,
.product-card:hover .product-overlay {
  opacity: 1;
}

.product-info,
.product-info {
  padding: 0.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  z-index: 2;
}

.product-title,
.product-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 0.5rem;
  line-height: 1.3;
  transition: color 0.3s ease;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
  height: 2.6rem;
}

.product-card:hover .product-title {
  color: #3b82f6;
}

.product-price,
.product-price {
  font-size: 1.2rem;
  font-weight: 800;
  background: linear-gradient(135deg, #0C5A85, #0a4563);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  margin-bottom: 1rem;
}

.product-type {
  font-size: 0.8rem;
  color: #6c757d;
  margin-bottom: 1rem;
  font-weight: 500;
}

@media (max-width: 768px) {
  .product-image,
  .product-image {
    height: 150px;
  }

  .product-title,
  .product-title {
    font-size: 0.9rem;
  }

  .product-price,
  .product-price {
    font-size: 1rem;
  }
}

/* Global Loading Screen Styles */
.global-loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: linear-gradient(135deg, #0f172a 0%, #1e293b 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  overflow: hidden;
}

.loading-container {
  text-align: center;
  color: white;
  max-width: 400px;
  padding: 1rem;
}

.loading-content {
  animation: fadeInUp 0.8s ease-out;
}

.loading-logo {
  margin-bottom: 2rem;
}

.loading-icon {
  font-size: 4rem;
  margin-bottom: 1rem;
  display: block;
  animation: pulse 2s infinite;
  color: #ffffff;
}

.loading-brand {
  font-size: 2.5rem;
  font-weight: 700;
  margin: 0;
  letter-spacing: 2px;
  color: #ffffff;
}

.loading-spinner {
  margin: 2rem 0;
  display: flex;
  justify-content: center;
}

.spinner-ring {
  display: inline-block;
  position: relative;
  width: 80px;
  height: 80px;
}

.spinner-ring div {
  box-sizing: border-box;
  display: block;
  position: absolute;
  width: 64px;
  height: 64px;
  margin: 8px;
  border: 4px solid #ffffff;
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
  border-color: #ffffff transparent transparent transparent;
}

.spinner-ring div:nth-child(1) {
  animation-delay: -0.45s;
}
.spinner-ring div:nth-child(2) {
  animation-delay: -0.3s;
}
.spinner-ring div:nth-child(3) {
  animation-delay: -0.15s;
}

.loading-text {
  margin-top: 1.5rem;
}

.loading-message {
  font-size: 1.2rem;
  margin-bottom: 1rem;
  font-weight: 300;
  color: #ffffff;
  opacity: 0.9;
}

.loading-progress {
  width: 100%;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.2);
  border-radius: 2px;
  overflow: hidden;
  margin-top: 1rem;
}

.progress-bar {
  height: 100%;
  background: linear-gradient(90deg, #ffffff 0%, #f8f9fa 50%, #ffffff 100%);
  border-radius: 2px;
  transition: width 0.3s ease;
  animation: shimmer 2s infinite;
}

/* Loading Animations */
@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
    transform: scale(1);
  }
  50% {
    opacity: 0.7;
    transform: scale(1.05);
  }
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200px 0;
  }
  100% {
    background-position: calc(200px + 100%) 0;
  }
}

/* Responsive Loading Screen */
@media (max-width: 768px) {
  .loading-container {
    padding: 0.5rem;
    max-width: 300px;
  }

  .loading-icon {
    font-size: 3rem;
  }

  .loading-brand {
    font-size: 2rem;
    letter-spacing: 1px;
  }

  .loading-message {
    font-size: 1rem;
  }

  .spinner-ring {
    width: 60px;
    height: 60px;
  }

  .spinner-ring div {
    width: 48px;
    height: 48px;
    margin: 6px;
    border-width: 3px;
  }
}

@media (max-width: 480px) {
  .loading-container {
    max-width: 250px;
  }

  .loading-icon {
    font-size: 2.5rem;
  }

  .loading-brand {
    font-size: 1.5rem;
  }
}

/* Posts/Blog Section Styles */
.featured-posts-section-wrapper {
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%);
}

.featured-posts-section {
  max-width: 1200px;
  margin: 0 auto;
}

.posts-title {
  font-size: 2.5rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  text-align: center;
}

.posts-subtitle {
  font-size: 1.1rem;
  color: #6c757d;
  text-align: center;
  margin-bottom: 3rem;
}

.posts-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;
  padding: 0 1rem;
}

.post-card {
  background: white;
  border-radius: 15px;
  border: none;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.post-card:hover {
  transform: translateY(-10px);
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15);
}

.post-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.post-category-badge {
  position: absolute;
  top: 1rem;
  left: 1rem;
  background: rgba(0, 123, 255, 0.9);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 600;
  z-index: 5;
}

.post-image {
  position: relative;
  width: 100%;
  height: 200px;
  overflow: hidden;
}

.post-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.post-card:hover .post-image img {
  transform: scale(1.05);
}

.post-content {
  padding: 0.75rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
}

.post-category {
  font-size: 0.8rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #007bff;
  margin-bottom: 0.5rem;
}

.post-title {
  font-size: 1.25rem;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 1rem;
  line-height: 1.4;
  flex-grow: 1;
}

.post-excerpt {
  color: #6c757d;
  line-height: 1.6;
  margin-bottom: 1rem;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: auto;
}

.post-date {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.post-views {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.post-read-more {
  font-size: 0.875rem;
  font-weight: 600;
  color: #007bff;
  text-decoration: none;
  transition: color 0.3s ease;
}

.post-read-more:hover {
  color: #0056b3;
}

/* Posts Carousel Styles */
.posts-carousel .carousel-item {
  padding: 0 15px;
}

.posts-carousel .carousel-control-prev,
.posts-carousel .carousel-control-next {
  width: 50px;
  height: 50px;
  background: rgba(0, 123, 255, 0.1);
  border-radius: 50%;
  top: 50%;
  transform: translateY(-50%);
}

.posts-carousel .carousel-control-prev {
  left: -25px;
}

.posts-carousel .carousel-control-next {
  right: -25px;
}

.posts-carousel .carousel-indicators {
  bottom: -50px;
}

.posts-carousel .carousel-indicators button {
  background-color: #007bff;
  border-radius: 50%;
  width: 12px;
  height: 12px;
  margin: 0 5px;
}

/* Grid Post Card Styles - Matching PostsView.vue */
.post-card-grid {
  background: white;
  border-radius: 15px;
  overflow: hidden;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.post-card-grid:hover {
  transform: translateY(-5px);
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
}

.post-card-grid .post-image-wrapper {
  position: relative;
  height: 200px;
  overflow: hidden;
}

.post-card-grid .post-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.post-card-grid:hover .post-image {
  transform: scale(1.05);
}

.post-card-grid .post-content {
  padding: 0.75rem;
  flex: 1;
  display: flex;
  flex-direction: column;
}

.post-card-grid .post-title {
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 1rem;
  line-height: 1.3;
}

.post-card-grid .post-title .post-link {
  color: #2c3e50;
  text-decoration: none;
  transition: color 0.3s ease;
}

.post-card-grid .post-title .post-link:hover {
  color: #007bff;
}

.post-card-grid .post-excerpt {
  color: #6c757d;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 1rem;
  flex: 1;
}

.post-card-grid .post-meta {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.8rem;
  color: #95a5a6;
  margin-bottom: 1rem;
}

.post-card-grid .post-date,
.post-card-grid .post-views {
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.post-card-grid .read-more-btn {
  margin-top: auto;
  align-self: flex-start;
}

/* Posts Mobile Responsive */
@media (max-width: 768px) {
  .posts-title {
    font-size: 2rem;
  }

  .posts-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
    padding: 0 1rem;
    margin: 0 auto;
  }

  .post-card {
    margin: 0;
  }

  .post-content {
    padding: 0.5rem;
  }

  .posts-carousel .carousel-control-prev,
  .posts-carousel .carousel-control-next {
    display: none;
  }
}

@media (max-width: 576px) {
  .posts-title {
    font-size: 1.75rem;
  }

  .posts-grid {
    grid-template-columns: 1fr;
    padding: 0 0.75rem;
  }

  .post-image {
    height: 180px;
  }

  .post-title {
    font-size: 1.1rem;
  }

  .read-more-btn {
    font-size: 0.75rem !important;
    padding: 0.25rem 0.5rem !important;
    border-radius: 0.25rem !important;
  }
}

/* Extra small screens - πολύ μικρές οθόνες */
@media (max-width: 400px) {
  .posts-grid {
    padding: 0 5%; /* 90% πλάτος για πολύ μικρές οθόνες */
    gap: 0.75rem;
  }

  .post-content {
    padding: 0.375rem;
  }

  .post-title {
    font-size: 1rem;
  }

  .read-more-btn {
    font-size: 0.7rem !important;
    padding: 0.2rem 0.4rem !important;
  }
}

/* Floating Circles Pattern Background */
.floating-circles-pattern {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 0;
  pointer-events: none;
  background-image:
    radial-gradient(
      circle at 25% 25%,
      var(--pattern-color, rgba(255, 255, 255, 0.1)) 0%,
      transparent 50%
    ),
    radial-gradient(
      circle at 75% 75%,
      var(--pattern-color, rgba(255, 255, 255, 0.05)) 0%,
      transparent 50%
    );
  background-size: 400px 400px;
  animation: floatCircles 20s ease-in-out infinite;
}

@keyframes floatCircles {
  0%,
  100% {
    transform: translate(0, 0) rotate(0deg);
  }
  33% {
    transform: translate(30px, -30px) rotate(120deg);
  }
  66% {
    transform: translate(-20px, 20px) rotate(240deg);
  }
}

/* Apply pattern color variable when pattern type is floating-circles */
.extra-section[data-pattern='floating-circles'] {
  --pattern-color: var(--ball-color-1, rgba(255, 255, 255, 0.1));
}

/* Animated Border Gradient Glow Effect for Gallery Items */
@keyframes glowPulse {
  0%, 100% {
    box-shadow: 0 0 10px rgba(255, 107, 107, 0.6),
                0 0 20px rgba(78, 205, 196, 0.4),
                0 0 30px rgba(69, 183, 209, 0.2);
  }
  50% {
    box-shadow: 0 0 20px rgba(255, 107, 107, 0.8),
                0 0 30px rgba(78, 205, 196, 0.6),
                0 0 40px rgba(69, 183, 209, 0.4);
  }
}

.gallery-item.gradient-glow {
  animation: glowPulse 2s ease-in-out infinite !important;
  box-shadow: 0 0 15px rgba(255, 107, 107, 0.7),
              0 0 25px rgba(78, 205, 196, 0.5),
              0 0 35px rgba(69, 183, 209, 0.3) !important;
}

/* Home Builder gallery widget — respect imageHeight on the public homepage */
:deep(.gallery-widget) {
  height: auto !important;
  max-height: none !important;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  box-sizing: border-box;
}

:deep(.gallery-widget.mobile-layout-carousel .pb-gallery-grid) {
  width: 100%;
  min-width: 0;
}

:deep(.gallery-widget.mobile-layout-carousel .gallery-carousel-track) {
  max-width: none !important;
}

:deep(.enhanced-text-widget.has-background-image) {
  position: relative !important;
  overflow: hidden !important;
}

:deep(.enhanced-text-widget.has-background-image .background-image-layer) {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
}

:deep(.gallery-widget .image-wrapper) {
  height: var(--pb-gallery-image-height, 200px) !important;
  min-height: var(--pb-gallery-image-height, 200px) !important;
  max-height: var(--pb-gallery-image-height, 200px) !important;
}

:deep(.gallery-widget .gallery-image) {
  width: 100% !important;
  height: 100% !important;
  max-height: 100% !important;
  object-fit: contain !important;
  object-position: center !important;
}

:deep(.gallery-widget.mobile-layout-carousel .image-wrapper) {
  display: flex !important;
  align-items: center;
  justify-content: center;
  background: transparent;
}

:deep(.gallery-widget.mobile-layout-carousel .gallery-image) {
  width: auto !important;
  height: auto !important;
  max-width: 100% !important;
  max-height: 100% !important;
  min-height: 0 !important;
  object-fit: contain !important;
  object-position: center !important;
}
</style>
